// File: chat.js
// Amaç:
// - İlk yüklemede son mesajları JSON API üzerinden almak ve anlık gösterim için SessionStorage (String Caching) kullanmak
// - Yukarı kaydırınca eski mesajları almak (Throttled)
// - Yeni mesajları periyodik olarak senkronize etmek
// - Mesaj gönderme (Enter desteği), mobil yükseklik ve global request kart güncellemelerini tek dosyada yönetmek
// - Görseller yüklendiğinde otomatik en aşağı kaydırmayı garantiye almak

(function (window, document, $) {
    "use strict";

    if (!$) return;

    // --- UTILS ---
    const throttle = (func, limit) => {
        let inThrottle;
        return function (...args) {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => (inThrottle = false), limit);
            }
        };
    };

    const ChatModule = {
        config: {
            messagesAreaSelector: "#MessagesArea",
            messagesAreaFallbackSelector: ".messages-area",
            chatIdSelector: "#ChatId",
            currentUserIdSelector: "#CurrentUserId",
            formSelector: "#chatForm",
            textareaSelector: "#messageText",
            imageInputSelector: "#imageInput",
            videoInputSelector: "#videoInput",
            gifInputSelector: "#gifInput",
            stickerInputSelector: "#stickerInput",
            fileInputSelector: "#fileInput",
            topUserSelector: ".top-user-info",
            chatInputSelector: ".chat-input-container",
            partialBodySelector: "#_partialBody",
            openProfilePageSelector: ".OpenChatProfilePage",
            mobileHeaderSelector: "#rtsMobileHeader",
            mobileFooterSelector: ".mobileFooterBar, #mobileFooterBar",
            sendAction: "/UsersMessage/SendMessage",
            messageInfoAction: "/GetInfoChatMessage",
            editMessageAction: "/EditChatMessage",
            updatedMessagesAction: "/GetUpdatedChatMessages",
            reportMessageAction: "/ReportMessage",
            withdrawReportAction: "/WithdrawMessageReport",
            screenshotMessageAction: "/AddScreenshotSystemMessage",
            deleteMessageAction: "/DeleteChatMessage",
            deletedMessagesAction: "/GetDeletedChatMessageIds",
            forwardMessageAction: "/ForwardChatMessage",
            pageSize: 30,
            pollIntervalMs: 4000,
            topThresholdPx: 80,
            bottomThresholdPx: 120,
            mobileFooterFallbackHeightPx: 53,
            minMessagesHeightPx: 180,
            minInputHeightPx: 58,
            timeRevealDistancePx: 76,
            timeRevealHoldMs: 900,
            longPressDelayMs: 520,
            longPressMoveTolerancePx: 12,
            deleteWindowMinutes: 5,
            changelogObjectTypeId: 91,
            deletedSyncMaxIds: 160,
            updatedSyncMaxIds: 160,
            keyboardInsetThresholdPx: 90,
            keyboardAdjustDelayMs: 24,
            keyboardAnimationMs: 210,
            minKeyboardMessagesHeightPx: 118,
            reservationAnnouncementActionTypeId: 20
        },

        state: {
            chatId: 0,
            currentUserId: 0,
            isInitialized: false,
            isLoadingInitial: false,
            isLoadingOlder: false,
            isSyncingNew: false,
            isSyncingUpdatedMessages: false,
            isSending: false,
            hasMoreOlder: true,
            isNearBottom: true,
            baseMessagesHeight: 0,
            oldestCursor: null,
            newestCursor: null,
            pollTimer: null,
            resizeTimer: null,
            scrollAnimationFrame: null,
            scrollStopTimer: null,
            timeRevealAnimationFrame: null,
            timeRevealCloseTimer: null,
            timeRevealHandlers: null,
            isTimeRevealDragging: false,
            isTimeRevealLocked: false,
            timeRevealPointerId: null,
            timeRevealStartX: 0,
            timeRevealStartY: 0,
            timeRevealProgress: 0,
            lastScrollTop: 0,
            lastScrollTime: 0,
            currentShift: 0,
            targetShift: 0,
            currentScale: 1,
            targetScale: 1,
            currentRotate: 0,
            targetRotate: 0,
            isMotionLoopRunning: false,
            isKeyboardOpen: false,
            viewportResizeHandler: null,
            viewportScrollHandler: null,
            virtualKeyboardGeometryHandler: null,
            virtualKeyboardOverlayOriginal: null,
            viewportLayoutManaged: false,
            messageActionTimer: null,
            messageActionPointerId: null,
            messageActionStartX: 0,
            messageActionStartY: 0,
            activeMessageActionId: null,
            isMessageInfoLoading: false,
            isDeletingMessage: false,
            isSyncingDeletedMessages: false,
            currentUserIsChatAdmin: false,
            isEditingMessage: false,
            isReportingMessage: false,
            isForwardingMessage: false,
            forwardMessageId: 0,
            isWithdrawingReport: false,
            isEmojiPanelOpen: false,
            isAttachmentMenuOpen: false,
            screenshotNoticeLock: false,
            keyboardBaselineHeight: 0,
            keyboardInsetPx: 0,
            keyboardFocusTimer: null,
            keyboardLayoutFrame: null,
            keyboardFocusStartedAt: 0,
            keyboardSettleTimers: [],
            keyboardViewportAnchor: null,
            editingMessageId: 0,
            editingOriginalText: "",
            isRestoringOlderScroll: false,
            olderScrollAnchor: null,
            olderScrollRestoreFrame: null,
            initialBottomPinActive: false,
            initialBottomPinCompleted: false,
            initialBottomPinObserver: null,
            initialBottomPinFrame: null,
            initialBottomPinSettleTimer: null,
        },

        init: function () {
            if (this.state.isInitialized) return;

            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return;

            $('#DesktopContainer').addClass('chatCompability');

            this.state.chatId = this.resolveChatId($messagesArea);

            if (this.state.chatId <= 0) {
                this.showError("Sohbet bilgisi alınamadı.");
                return;
            }

            this.ensureChatForm();
            this.activateManagedViewportLayout();

            this.state.isInitialized = true;
            this.state.isNearBottom = true;
            this.ensureScrollStabilityStyles();
            this.state.lastScrollTop = $messagesArea.scrollTop() || 0;
            this.state.lastScrollTime = Date.now();

            this.bindEvents();
            this.bindViewportEvents();
            this.adjustHeight(false);
            this.loadInitial();
        },

        destroy: function () {
            const $messagesArea = this.getMessagesArea();
            const $textarea = $(this.config.textareaSelector);

            this.stopPolling();

            if (this.state.resizeTimer) window.clearTimeout(this.state.resizeTimer);
            if (this.state.scrollStopTimer) window.clearTimeout(this.state.scrollStopTimer);
            if (this.state.scrollAnimationFrame) window.cancelAnimationFrame(this.state.scrollAnimationFrame);
            if (this.state.timeRevealAnimationFrame) window.cancelAnimationFrame(this.state.timeRevealAnimationFrame);
            if (this.state.timeRevealCloseTimer) window.clearTimeout(this.state.timeRevealCloseTimer);
            if (this.state.messageActionTimer) window.clearTimeout(this.state.messageActionTimer);
            if (this.state.keyboardFocusTimer) window.clearTimeout(this.state.keyboardFocusTimer);
            if (this.state.keyboardLayoutFrame) window.cancelAnimationFrame(this.state.keyboardLayoutFrame);
            this.clearKeyboardSettleTimers();
            this.finishInitialBottomPinning();

            this.cancelComposerEdit(false);
            this.closeMessageActionPopover();
            this.closeAttachmentMenu();
            this.unbindMessageActionEvents();
            this.unbindViewportEvents();
            this.unbindTimeRevealEvents();
            this.setTimeRevealProgress(0, true);
            this.resetKeyboardLayout();
            this.deactivateManagedViewportLayout();

            $messagesArea.off("scroll.chat");
            $textarea.off("pointerdown.chat focus.chat blur.chat input.chat keydown.chat");
            $(document).off("submit.chat", this.config.formSelector);
            $(document).off("click.chatAttachment change.chatAttachment click.chatEmoji click.chatMessageActions chat:screenshot.chat");
            $(document).off("globalRequestCard:loaded.chatBottomPin");
            $(window).off("chatScreenshotTaken.chat screenshot.chat");
            $(document).off("visibilitychange.chat");
            $(window).off("focus.chat blur.chat resize.chat beforeunload.chat");

            this.state = { ...this.state, isInitialized: false, isLoadingInitial: false, isLoadingOlder: false, isSyncingNew: false, isSyncingUpdatedMessages: false, isSending: false, isKeyboardOpen: false, viewportLayoutManaged: false, pollTimer: null, initialBottomPinActive: false, initialBottomPinCompleted: false, initialBottomPinObserver: null, initialBottomPinFrame: null, initialBottomPinSettleTimer: null };
        },

        shouldManageViewportLayout: function () {
            const hasNativeBridge = !!(
                window.jsBridge ||
                window.webkit?.messageHandlers?.nativeBridge ||
                window.webkit?.messageHandlers?.isFullScreenApp
            );
            const hasCoarsePointer = typeof window.matchMedia === "function" &&
                window.matchMedia("(hover: none) and (pointer: coarse)").matches;
            const hasTouch = (navigator.maxTouchPoints || 0) > 0;

            return hasNativeBridge || hasCoarsePointer || hasTouch || window.innerWidth <= 768;
        },

        activateManagedViewportLayout: function () {
            const $messagesArea = this.getMessagesArea();
            const $shell = $messagesArea.closest(".container-bg").first();
            if ($messagesArea.length === 0 || $shell.length === 0) return;
            if ($shell.attr("data-chat-viewport-page") !== "true") return;
            if (!this.shouldManageViewportLayout()) return;

            this.state.viewportLayoutManaged = true;
            document.documentElement.classList.add("chat-viewport-managed-root");
            document.body.classList.add("chat-viewport-managed");
            $shell.addClass("chat-viewport-shell");
            this.updateManagedViewportLayout();
        },

        deactivateManagedViewportLayout: function () {
            const $shell = $(".chat-viewport-shell");
            $shell.each(function () {
                this.style.removeProperty("--chat-shell-height");
                this.style.removeProperty("--chat-shell-lock-top");
                this.style.removeProperty("--chat-shell-lock-left");
                this.style.removeProperty("--chat-shell-lock-width");
                this.style.removeProperty("--chat-vv-offset-top");
                this.style.removeProperty("--chat-vv-offset-left");
            }).removeClass("chat-viewport-shell");

            document.documentElement.classList.remove("chat-viewport-managed-root");
            document.body.classList.remove("chat-viewport-managed", "chat-ios-keyboard-locked");
            this.state.keyboardViewportAnchor = null;
            this.state.viewportLayoutManaged = false;
        },

        updateManagedViewportLayout: function (virtualKeyboardRect) {
            if (!this.state.viewportLayoutManaged) return;

            const $messagesArea = this.getMessagesArea();
            const $shell = $messagesArea.closest(".chat-viewport-shell").first();
            if ($messagesArea.length === 0 || $shell.length === 0) return;

            const shellElement = $shell.get(0);
            const viewport = window.visualViewport;
            const layoutHeight = Math.max(
                document.documentElement.clientHeight || 0,
                window.innerHeight || 0
            );
            const visualOffsetTop = viewport && Number.isFinite(viewport.offsetTop)
                ? Math.max(0, viewport.offsetTop)
                : 0;
            const visualOffsetLeft = viewport && Number.isFinite(viewport.offsetLeft)
                ? Math.max(0, viewport.offsetLeft)
                : 0;
            const visualHeight = viewport && viewport.height
                ? viewport.height
                : (window.innerHeight || layoutHeight);
            let visibleBottom = visualOffsetTop + visualHeight;

            const textareaFocused = document.activeElement && document.activeElement.id === "messageText";
            const keyboardRect = virtualKeyboardRect ||
                (navigator.virtualKeyboard && navigator.virtualKeyboard.boundingRect
                    ? navigator.virtualKeyboard.boundingRect
                    : null);
            const keyboardHeight = keyboardRect
                ? Math.max(0, Number(keyboardRect.height) || 0)
                : 0;

            if (textareaFocused && keyboardHeight > this.config.keyboardInsetThresholdPx) {
                const keyboardTop = Number(keyboardRect.top ?? keyboardRect.y);
                const fallbackKeyboardTop = Math.max(0, layoutHeight - keyboardHeight);
                visibleBottom = Math.min(
                    visibleBottom,
                    Number.isFinite(keyboardTop) && keyboardTop > 0 ? keyboardTop : fallbackKeyboardTop
                );
            }

            /*
             * iOS/WebKit, özellikle iOS 26 WKWebView/Safari'de, klavye açılırken layout
             * viewport sabit kalıp visual viewport yukarı pan edilebiliyor. Bu durumda
             * getBoundingClientRect() negatif yönde kayarken offsetTop artıyor ve yalnızca
             * "visibleBottom - shell.top" hesabı sohbet kabuğunu gereğinden fazla büyütüyor.
             *
             * Çözüm: klavye açılmadan hemen önce alınan sohbet kabuğu geometrisini koru,
             * kabuğu visual viewport'a device-fixed benzeri şekilde kilitle ve yüksekliği
             * doğrudan görünen viewport yüksekliğine göre hesapla. Android davranışı aynen
             * korunur; bu kilit yalnızca iOS benzeri cihazlarda ve textarea odaktayken aktiftir.
             */
            const shouldLockIOSViewport = this.isIOSLikeDevice() && textareaFocused && this.state.isKeyboardOpen;
            if (shouldLockIOSViewport) {
                if (!this.state.keyboardViewportAnchor) {
                    this.captureKeyboardViewportAnchor();
                }

                const anchor = this.state.keyboardViewportAnchor;
                if (anchor) {
                    const top = Math.max(0, Math.round(anchor.top || 0));
                    const left = Math.max(0, Math.round(anchor.left || 0));
                    const width = Math.max(1, Math.round(anchor.width || shellElement.getBoundingClientRect().width || window.innerWidth || 1));
                    const shellHeight = Math.max(120, Math.floor(visualHeight - top));

                    document.body.classList.add("chat-ios-keyboard-locked");
                    shellElement.style.setProperty("--chat-shell-lock-top", `${top}px`);
                    shellElement.style.setProperty("--chat-shell-lock-left", `${left}px`);
                    shellElement.style.setProperty("--chat-shell-lock-width", `${width}px`);
                    shellElement.style.setProperty("--chat-vv-offset-top", `${Math.round(visualOffsetTop)}px`);
                    shellElement.style.setProperty("--chat-vv-offset-left", `${Math.round(visualOffsetLeft)}px`);
                    shellElement.style.setProperty("--chat-shell-height", `${shellHeight}px`);
                    return;
                }
            }

            this.clearManagedViewportKeyboardLock(false);

            if (!this.state.isKeyboardOpen) {
                $(this.config.mobileFooterSelector).each(function () {
                    if (!ChatModule.isElementVisible($(this))) return true;
                    const footerRect = this.getBoundingClientRect();
                    if (footerRect.height > 0 && footerRect.top > 0) {
                        visibleBottom = Math.min(visibleBottom, footerRect.top);
                    }
                    return false;
                });
            }

            const shellRect = shellElement.getBoundingClientRect();
            const shellHeight = Math.max(120, Math.floor(visibleBottom - shellRect.top));
            shellElement.style.setProperty("--chat-shell-height", `${shellHeight}px`);
        },

        isIOSLikeDevice: function () {
            const platform = navigator.platform || "";
            const userAgent = navigator.userAgent || "";
            return /iPad|iPhone|iPod/i.test(userAgent) ||
                (platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
        },

        captureKeyboardViewportAnchor: function () {
            if (!this.state.viewportLayoutManaged || !this.isIOSLikeDevice()) return;
            if (this.state.isKeyboardOpen && this.state.keyboardViewportAnchor) return;

            const $shell = this.getMessagesArea().closest(".chat-viewport-shell").first();
            if ($shell.length === 0) return;

            const shellElement = $shell.get(0);
            const rect = shellElement.getBoundingClientRect();
            const bodyRect = document.body ? document.body.getBoundingClientRect() : null;
            const bodyShiftX = bodyRect && Number.isFinite(bodyRect.left) && bodyRect.left < 0 ? bodyRect.left : 0;
            const bodyShiftY = bodyRect && Number.isFinite(bodyRect.top) && bodyRect.top < 0 ? bodyRect.top : 0;

            this.state.keyboardViewportAnchor = {
                top: Math.max(0, Math.round((rect.top || 0) - bodyShiftY)),
                left: Math.max(0, Math.round((rect.left || 0) - bodyShiftX)),
                width: Math.max(1, Math.round(rect.width || window.innerWidth || 1))
            };
        },

        clearManagedViewportKeyboardLock: function (clearAnchor) {
            document.body.classList.remove("chat-ios-keyboard-locked");

            const $shell = this.getMessagesArea().closest(".chat-viewport-shell").first();
            if ($shell.length) {
                const shellElement = $shell.get(0);
                shellElement.style.removeProperty("--chat-shell-lock-top");
                shellElement.style.removeProperty("--chat-shell-lock-left");
                shellElement.style.removeProperty("--chat-shell-lock-width");
                shellElement.style.removeProperty("--chat-vv-offset-top");
                shellElement.style.removeProperty("--chat-vv-offset-left");
            }

            if (clearAnchor) this.state.keyboardViewportAnchor = null;
        },

        clearKeyboardSettleTimers: function () {
            const timers = this.state.keyboardSettleTimers || [];
            timers.forEach(timer => window.clearTimeout(timer));
            this.state.keyboardSettleTimers = [];
        },

        scheduleKeyboardSettleUpdates: function (forceScrollToBottom) {
            this.clearKeyboardSettleTimers();

            const delays = [0, 60, 140, 260, 420, 650];
            this.state.keyboardSettleTimers = delays.map(delay => window.setTimeout(() => {
                const textareaFocused = document.activeElement && document.activeElement.id === "messageText";
                if (!textareaFocused) return;
                this.requestKeyboardLayoutUpdate(!!forceScrollToBottom && delay >= 260);
            }, delay));
        },

        bindEvents: function () {
            const self = this;
            const $messagesArea = self.getMessagesArea();
            const $textarea = $(self.config.textareaSelector);

            // 10/10 Performans: Ağır dom/arka plan kontrolleri optimize edilerek throttle'da kalıyor
            const throttledHeavyScroll = throttle(function () {
                if (self.state.isRestoringOlderScroll) return;
                self.state.isNearBottom = self.isNearBottom();
                self.updateActiveDateBadge();
                if (($messagesArea.scrollTop() || 0) <= self.config.topThresholdPx) {
                    self.loadOlderMessages();
                }
            }, 120);

            // Fizik motoru için unthrottled kesintisiz scroll takibi (Asenkron RAF tetikleyicisi)
            $messagesArea.off("scroll.chat").on("scroll.chat", function () {
                if (self.state.isRestoringOlderScroll) return;
                const scrollTop = $messagesArea.scrollTop() || 0;

                // İvme hesaplama motorunu tetikle (Hafif operasyon)
                self.handleScrollMotion(scrollTop);

                // Ağır fonksiyonları tetikle (Throttle korumalı)
                throttledHeavyScroll();
            });

            self.bindTimeRevealEvents();
            self.bindMessageActionEvents();

            $(window)
                .off("focus.chat").on("focus.chat", () => { self.startPolling(); self.syncNewMessages(true); self.syncDeletedMessages(); self.syncUpdatedMessages(); })
                .off("blur.chat beforeunload.chat").on("blur.chat beforeunload.chat", () => self.stopPolling())
                .off("resize.chat").on("resize.chat", () => { self.updateKeyboardLayout(); self.scheduleHeightAdjust(false); });

            $(document)
                .off("visibilitychange.chat").on("visibilitychange.chat", () => document.hidden ? self.stopPolling() : (self.startPolling(), self.syncNewMessages(true), self.syncDeletedMessages(), self.syncUpdatedMessages()));

            $textarea
                .off("pointerdown.chat").on("pointerdown.chat", function () { self.captureKeyboardViewportAnchor(); })
                .off("focus.chat").on("focus.chat", function () { self.onTextareaFocus(); })
                .off("blur.chat").on("blur.chat", function () { self.onTextareaBlur(); })
                .off("input.chat").on("input.chat", function () {
                    self.autoResizeTextarea(this);
                    self.scheduleHeightAdjust(false);
                })
                .off("keydown.chat").on("keydown.chat", function (e) {
                    if (e.which === 13 && !e.shiftKey) {
                        e.preventDefault();
                        $(self.config.formSelector).submit();
                    }
                });

            self.autoResizeTextarea($textarea.get(0));

            $(document)
                .off("submit.chat", self.config.formSelector)
                .on("submit.chat", self.config.formSelector, function (event) { self.onFormSubmit(event, this); });

            $(document)
                .off("click.chatAttachment")
                .on("click.chatAttachment", "[data-chat-attachment-menu-toggle]", function (event) {
                    event.preventDefault();
                    if (self.state.editingMessageId > 0 || self.state.isSending) return;
                    self.toggleAttachmentMenu();
                })
                .on("click.chatAttachment", "[data-chat-file-trigger]", function (event) {
                    event.preventDefault();
                    if (self.state.editingMessageId > 0 || self.state.isSending) return;
                    self.closeAttachmentMenu();
                    self.triggerAttachmentInput($(this).data("chat-file-trigger"));
                })
                .on("click.chatAttachment", "[data-chat-cancel-edit-mode]", function (event) {
                    event.preventDefault();
                    self.cancelComposerEdit(true);
                })
                .on("click.chatAttachment", "[data-chat-clear-files]", function (event) {
                    event.preventDefault();
                    self.clearSelectedFiles();
                })
                .on("click.chatAttachment", function (event) {
                    const $target = $(event.target);
                    if ($target.closest(".chat-attachment-menu, [data-chat-attachment-menu-toggle]").length === 0) {
                        self.closeAttachmentMenu();
                    }
                });

            $(document)
                .off("click.chatEmoji")
                .on("click.chatEmoji", "[data-chat-emoji-toggle]", function (event) {
                    event.preventDefault();
                    self.toggleEmojiPanel();
                })
                .on("click.chatEmoji", "[data-chat-emoji]", function (event) {
                    event.preventDefault();
                    self.insertEmojiAtCursor($(this).data("chat-emoji"));
                })
                .on("click.chatEmoji", function (event) {
                    const $target = $(event.target);
                    if ($target.closest(".chat-emoji-panel, [data-chat-emoji-toggle]").length === 0) {
                        self.closeEmojiPanel();
                    }
                });

            $(document)
                .off("chat:screenshot.chat")
                .on("chat:screenshot.chat", function () { self.notifyScreenshotTaken(); });

            $(document)
                .off("globalRequestCard:loaded.chatBottomPin")
                .on("globalRequestCard:loaded.chatBottomPin", ".globalRequestCard", function (_event, result) {
                    self.onGlobalRequestCardLoaded(this, result);
                });

            $(window)
                .off("chatScreenshotTaken.chat screenshot.chat")
                .on("chatScreenshotTaken.chat screenshot.chat", function () { self.notifyScreenshotTaken(); });

            $(document)
                .off("change.chatAttachment")
                .on("change.chatAttachment", `${self.config.imageInputSelector}, ${self.config.videoInputSelector}, ${self.config.gifInputSelector}, ${self.config.stickerInputSelector}, ${self.config.fileInputSelector}`, function () {
                    self.updateSelectedFileInfo();
                    self.scheduleHeightAdjust(false);
                });
        },

        loadInitial: function () {
            const self = this;
            if (self.state.isLoadingInitial) return;

            self.state.isLoadingInitial = true;

            // String Caching: Anlık render (Işık hızı hissi)
            const cacheKey = `chat_cache_${self.state.chatId}`;
            try {
                const cachedHtml = sessionStorage.getItem(cacheKey);
                if (cachedHtml) {
                    self.getMessagesArea().html(cachedHtml);
                    self.scrollToBottom(false);
                }
            } catch (e) { console.warn("Cache okuma hatası", e); }

            self.resolveCurrentUserId()
                .then(currentUserId => {
                    self.state.currentUserId = parseInt(currentUserId, 10) || 0;
                    if (self.state.currentUserId <= 0) {
                        self.showError("Kullanıcı bilgisi alınamadı. Lütfen tekrar deneyin.");
                        self.state.isLoadingInitial = false;
                        return;
                    }
                    self.requestInitialMessages();
                })
                .catch(() => {
                    self.showError("Kullanıcı bilgisi alınamadı. Lütfen tekrar deneyin.");
                    self.state.isLoadingInitial = false;
                });
        },

        requestInitialMessages: function () {
            const self = this;
            if (self.getMessagesArea().children(".chat-message-item").length === 0) {
                self.showLoader("Mesajlar yükleniyor...");
            }

            $.ajax({
                url: "/GetChatMessagesInitial",
                type: "POST",
                data: { chatId: self.state.chatId, pageSize: self.config.pageSize },
                success: function (response) {
                    const payload = self.normalizeResponse(response);
                    if (!payload.success) {
                        self.showError(payload.message || "Mesajlar yüklenemedi.");
                        return;
                    }

                    const messages = self.sortMessages(payload.messages || []);
                    self.state.hasMoreOlder = !!payload.hasMoreOlder;
                    self.state.oldestCursor = self.normalizeCursor(payload.oldestCursor) || self.getCursorFromFirstMessage(messages);
                    self.state.newestCursor = self.normalizeCursor(payload.newestCursor) || self.getCursorFromLastMessage(messages);
                    self.state.currentUserIsChatAdmin = !!payload.currentUserIsChatAdmin;

                    self.renderInitialMessages(messages);
                    self.afterMessagesRendered(true);

                    // Önbelleği güncelle
                    try {
                        sessionStorage.setItem(`chat_cache_${self.state.chatId}`, self.getMessagesArea().html());
                    } catch (e) { console.warn("Cache yazma hatası", e); }

                    self.startPolling();
                },
                error: function (xhr) {
                    self.showError(self.getErrorMessage(xhr, "Mesajlar yüklenemedi."));
                },
                complete: function () {
                    self.hideLoader();
                    self.state.isLoadingInitial = false;
                }
            });
        },

        renderInitialMessages: function (messages) {
            const $messagesArea = this.getMessagesArea();
            $messagesArea.empty();

            if (!messages || messages.length === 0) {
                $messagesArea.append(this.renderEmptyState());
                this.adjustHeight(true);
                return;
            }

            $messagesArea.append(this.buildMessagesHtml(messages, null));
            this.state.isNearBottom = true;
            this.scrollToBottom(false);
            updateGlobalObjectCards();
            this.adjustHeight(true);
        },

        onScroll: function () {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return;

            const scrollTop = $messagesArea.scrollTop();
            this.state.isNearBottom = this.isNearBottom();
            this.handleScrollMotion(scrollTop);
            this.updateActiveDateBadge();

            if (scrollTop <= this.config.topThresholdPx) {
                this.loadOlderMessages();
            }
        },

        loadOlderMessages: function () {
            const self = this;
            if (self.state.isLoadingOlder || !self.state.hasMoreOlder || !self.state.oldestCursor) return;

            self.state.isLoadingOlder = true;
            self.state.isRestoringOlderScroll = true;

            const $messagesArea = self.getMessagesArea();
            const scrollAnchor = self.captureScrollAnchor();
            self.state.olderScrollAnchor = scrollAnchor;

            self.showTopLoader();

            $.ajax({
                url: "/GetOlderChatMessages",
                type: "POST",
                data: {
                    chatId: self.state.chatId,
                    beforeSendDate: self.state.oldestCursor.sendDate,
                    beforeMessageId: self.state.oldestCursor.usersMessageId,
                    pageSize: self.config.pageSize
                },
                success: function (response) {
                    const payload = self.normalizeResponse(response);
                    if (!payload.success) return;

                    const messages = self.filterNewMessages(self.sortMessages(payload.messages || []));

                    if (messages.length > 0) {
                        self.prepareForPrepend(messages);

                        const $newBatch = $(self.buildMessagesHtml(messages, null));
                        $messagesArea.prepend($newBatch);

                        self.state.oldestCursor = self.normalizeCursor(payload.oldestCursor) || self.getCursorFromFirstMessage(messages) || self.state.oldestCursor;
                        self.state.hasMoreOlder = !!payload.hasMoreOlder;

                        self.watchPrependedMedia($newBatch);
                        self.afterOlderMessagesRendered();
                        self.scheduleOlderScrollRestore(scrollAnchor);
                    } else {
                        self.state.hasMoreOlder = false;
                        self.afterOlderMessagesRendered();
                    }
                },
                error: function (xhr) { window.console && window.console.error("loadOlderMessages Hatası:", xhr); },
                complete: function () {
                    self.hideTopLoader();
                    self.state.isLoadingOlder = false;
                    window.setTimeout(() => {
                        if (!self.state.olderScrollAnchor) self.state.isRestoringOlderScroll = false;
                    }, 120);
                }
            });
        },

        syncNewMessages: function (forceScrollToBottom) {
            const self = this;
            if (self.state.isSyncingNew || !self.state.newestCursor) return;

            self.state.isSyncingNew = true;

            $.ajax({
                url: "/GetNewChatMessages",
                type: "POST",
                data: {
                    chatId: self.state.chatId,
                    afterSendDate: self.state.newestCursor.sendDate,
                    afterMessageId: self.state.newestCursor.usersMessageId
                },
                success: function (response) {
                    const payload = self.normalizeResponse(response);
                    if (!payload.success) return;

                    const sortedMessages = self.sortMessages(payload.messages || []);
                    const hadExistingUpdates = self.updateExistingMessagesFromPayload(sortedMessages);
                    const messages = self.filterNewMessages(sortedMessages);
                    if (messages.length === 0) {
                        if (hadExistingUpdates) self.afterExistingMessagesUpdated();
                        return;
                    }

                    const $messagesArea = self.getMessagesArea();
                    const shouldStickToBottom = forceScrollToBottom || self.isNearBottom();
                    const previousDateKey = self.getLastMessageDateKey();

                    self.removeEmptyState();
                    $messagesArea.append(self.buildMessagesHtml(messages, previousDateKey));
                    self.state.newestCursor = self.normalizeCursor(payload.newestCursor) || self.getCursorFromLastMessage(messages) || self.state.newestCursor;

                    self.afterMessagesRendered(shouldStickToBottom);
                },
                error: function (xhr) { window.console && window.console.error("syncNewMessages Hatası:", xhr); },
                complete: function () {
                    self.state.isSyncingNew = false;
                    self.syncDeletedMessages();
                    self.syncUpdatedMessages();
                }
            });
        },

        syncUpdatedMessages: function () {
            const self = this;
            if (self.state.isSyncingUpdatedMessages || !self.state.chatId) return;

            const renderedIds = self.getRenderedMessageIds();
            const messageIds = renderedIds.slice(Math.max(renderedIds.length - self.config.updatedSyncMaxIds, 0));
            if (messageIds.length === 0) return;

            self.state.isSyncingUpdatedMessages = true;

            $.ajax({
                url: self.config.updatedMessagesAction,
                type: "POST",
                traditional: true,
                data: {
                    chatId: self.state.chatId,
                    messageIds: messageIds
                },
                success: function (response) {
                    const payload = self.normalizeResponse(response);
                    if (!payload.success) return;

                    const messages = self.sortMessages(payload.messages || []);
                    const changed = self.updateExistingMessagesFromPayload(messages);
                    if (changed) self.afterExistingMessagesUpdated();
                },
                error: function (xhr) { window.console && window.console.error("syncUpdatedMessages Hatası:", xhr); },
                complete: function () { self.state.isSyncingUpdatedMessages = false; }
            });
        },

        updateExistingMessagesFromPayload: function (messages) {
            let changed = false;
            (messages || []).forEach(message => {
                const messageId = this.getMessageId(message);
                if (!messageId || !this.messageExists(messageId)) return;

                const $item = this.getMessageItem(messageId);
                if (!$item.hasClass("is-changelog-card") && (this.isStructuredMessage(message) || this.isStructuredMessageElement($item))) return;
                if (!this.hasRenderedMessageChanged(messageId, message)) return;

                if (parseInt(this.state.editingMessageId, 10) === parseInt(messageId, 10)) return;
                if (parseInt(this.state.activeMessageActionId, 10) === parseInt(messageId, 10)) this.closeMessageActionPopover();

                if (this.replaceRenderedMessageFromModel(message, true)) changed = true;
            });
            return changed;
        },

        hasRenderedMessageChanged: function (messageId, message) {
            const $item = this.getMessageItem(messageId);
            if ($item.hasClass("is-changelog-card")) {
                const nextTitle = String(this.getValue(message, ["Message", "message"]) || "");
                const nextImage = this.extractMediaUrl(this.getValue(message, ["Image", "image"]));
                return $item.find(".chat-changelog-card__title").first().text() !== nextTitle ||
                    ($item.find(".chat-changelog-card__image").first().attr("src") || "") !== nextImage;
            }
            if ($item.length === 0 || this.isStructuredMessageElement($item) || this.isStructuredMessage(message)) return false;

            const nextText = this.formatMessageText(this.getValue(message, ["Message", "message"]) || "");
            const currentText = $item.find(".chat-text").first().html() || "";
            const currentEdited = String($item.attr("data-edited") || "false") === "true";
            const nextEdited = this.isMessageEdited(message);
            const currentReported = String($item.attr("data-reported") || "false") === "true";
            const nextReported = this.isMessageReported(message);
            const currentHasImage = String($item.attr("data-has-image") || "false") === "true";
            const nextHasImage = this.messageHasImage(message);
            const currentHasVideo = String($item.attr("data-has-video") || "false") === "true";
            const nextHasVideo = this.messageHasVideo(message);
            const currentHasFile = String($item.attr("data-has-file") || "false") === "true";
            const nextHasFile = this.messageHasFile(message);

            return currentText !== nextText ||
                currentEdited !== nextEdited ||
                currentReported !== nextReported ||
                currentHasImage !== nextHasImage ||
                currentHasVideo !== nextHasVideo ||
                currentHasFile !== nextHasFile;
        },

        replaceRenderedMessageFromModel: function (message, highlight) {
            const messageId = this.getMessageId(message);
            const $item = this.getMessageItem(messageId);
            if ($item.length === 0 || (!$item.hasClass("is-changelog-card") && (this.isStructuredMessageElement($item) || this.isStructuredMessage(message)))) return false;

            const $newItem = $(this.buildMessageHtml(message));
            if ($newItem.length === 0) return false;

            if (highlight) $newItem.addClass("is-edited-pulse");
            $item.replaceWith($newItem);
            window.setTimeout(() => $newItem.removeClass("is-edited-pulse"), 900);
            updateGlobalObjectCards();
            return true;
        },

        afterExistingMessagesUpdated: function () {
            this.normalizeMessageGroups();
            this.updateActiveDateBadge();
            this.applyFontSizeUtilityClasses();
            this.cacheMessagesHtml();
        },

        syncDeletedMessages: function () {
            const self = this;
            if (self.state.isSyncingDeletedMessages || !self.state.chatId) return;

            const messageIds = self.getRenderedMessageIds();
            if (messageIds.length === 0) return;

            self.state.isSyncingDeletedMessages = true;

            $.ajax({
                url: self.config.deletedMessagesAction,
                type: "POST",
                traditional: true,
                data: {
                    chatId: self.state.chatId,
                    messageIds: messageIds
                },
                success: function (response) {
                    const deletedIds = self.getValue(response, ["deletedMessageIds", "DeletedMessageIds"]) || [];
                    if (!deletedIds || deletedIds.length === 0) return;

                    self.removeDeletedMessageItems(deletedIds);
                },
                error: function (xhr) { window.console && window.console.error("syncDeletedMessages Hatası:", xhr); },
                complete: function () { self.state.isSyncingDeletedMessages = false; }
            });
        },

        getRenderedMessageIds: function () {
            const ids = [];
            this.getMessagesArea()
                .children(".chat-message-item[data-id]")
                .filter(function () { return $(this).hasClass("is-changelog-card") || !$(this).is(".is-system-notification, .is-action-card, .is-object-card"); })
                .each(function () {
                    const id = parseInt($(this).data("id"), 10) || 0;
                    if (id > 0) ids.push(id);
                });

            return ids.slice(Math.max(ids.length - this.config.deletedSyncMaxIds, 0));
        },

        removeDeletedMessageItems: function (deletedIds) {
            const self = this;
            const deletedSet = new Set((deletedIds || []).map(id => String(id)));
            if (deletedSet.size === 0) return;

            self.closeMessageActionPopover();

            self.getMessagesArea().children(".chat-message-item[data-id]").each(function () {
                const $item = $(this);
                if (deletedSet.has(String($item.data("id")))) {
                    if (self.isStructuredMessageElement($item) && !$item.hasClass("is-changelog-card")) return true;
                    $item.addClass("is-removing");
                    window.setTimeout(() => $item.remove(), 130);
                }
            });

            window.setTimeout(() => {
                self.normalizeMessageGroups();
                self.updateActiveDateBadge();
                self.cacheMessagesHtml();
            }, 160);
        },

        startPolling: function () {
            const self = this;
            if (self.state.pollTimer) return;
            self.state.pollTimer = window.setInterval(() => {
                self.syncNewMessages(false);
                self.syncDeletedMessages();
                self.syncUpdatedMessages();
            }, self.config.pollIntervalMs);
        },

        stopPolling: function () {
            if (this.state.pollTimer) {
                window.clearInterval(this.state.pollTimer);
                this.state.pollTimer = null;
            }
        },

        onFormSubmit: function (event, form) {
            event.preventDefault();
            const self = this;

            if (self.state.editingMessageId > 0) {
                self.saveComposerEdit();
                return;
            }

            if (self.state.isSending) return;

            const $form = $(form);
            const $textarea = $(self.config.textareaSelector);
            const message = ($textarea.val() || "").trim();
            const hasAttachment = self.hasAnySelectedAttachment();

            if (!message && !hasAttachment) return;

            const formData = new FormData(form);
            formData.set("MemberId", self.state.currentUserId || $(self.config.currentUserIdSelector).val() || "");
            formData.set("ChatId", self.state.chatId || $(self.config.chatIdSelector).val() || "");
            formData.set("Message", message);

            self.state.isSending = true;
            self.closeAttachmentMenu();
            self.closeEmojiPanel();
            self.setFormSending($form, true);

            $.ajax({
                url: $form.attr("action") || "/UsersMessage/SendMessage",
                method: ($form.attr("method") || "POST").toUpperCase(),
                data: formData,
                processData: false,
                contentType: false,
                xhr: function () {
                    const xhr = $.ajaxSettings.xhr();
                    if (xhr.upload) {
                        xhr.upload.addEventListener("progress", function (event) {
                            if (!event.lengthComputable) return;
                            const percent = Math.max(1, Math.min(99, Math.round((event.loaded / event.total) * 100)));
                            self.updateSendButtonProgress($form, percent);
                        }, false);
                    }
                    return xhr;
                }
            })
                .done(() => {
                    self.clearFormInputs();
                    if (self.state.newestCursor) self.syncNewMessages(true);
                    else self.requestInitialMessages();
                })
                .fail(xhr => {
                    if (!xhr.responseJSON?.moderationWarning && !xhr.responseJSON?.accountRestricted) self.showError(self.getErrorMessage(xhr, "Mesaj gönderilemedi."));
                    window.console && window.console.error("sendMessage Hatası:", xhr);
                })
                .always(() => {
                    self.state.isSending = false;
                    self.setFormSending($form, false);
                });
        },

        adjustHeight: function (forceScrollToBottom) {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return;

            this.updateKeyboardLayout();

            const wasNearBottom = this.isNearBottom();
            const shouldStickToBottom = forceScrollToBottom || wasNearBottom;

            if (this.state.viewportLayoutManaged) {
                this.updateManagedViewportLayout();
                $messagesArea.css({
                    height: "",
                    maxHeight: "",
                    paddingBottom: this.state.isKeyboardOpen ? "8px" : ""
                });
                this.state.baseMessagesHeight = Math.max(0, $messagesArea.get(0).clientHeight || 0);

                if (shouldStickToBottom) {
                    this.scrollToBottom(this.state.isKeyboardOpen);
                }

                this.updateActiveDateBadge();
                return;
            }

            const $chatInput = $(this.config.chatInputSelector).first();
            const inputHeight = $chatInput.length ? ($chatInput.outerHeight(true) || this.config.minInputHeightPx) : this.config.minInputHeightPx;
            const footerHeight = this.getMobileFooterHeight();
            const profileHeight = this.getOpenProfileHeight();
            const viewportHeight = this.getViewportHeight();
            const partialBodyHeight = this.getPartialBodyHeight();
            const usableBodyHeight = Math.min(partialBodyHeight || viewportHeight, viewportHeight || partialBodyHeight);
            const safeArea = this.getSafeAreaInsets();
            // Alt inset giriş alanının padding/outerHeight değerine zaten dahildir;
            // burada yalnızca üst güvenli alanı ayırarak alt boşluğu iki kez düşmeyiz.
            const safeAreaReserve = this.state.isKeyboardOpen ? 0 : safeArea.top;
            const compactReserve = this.state.isKeyboardOpen ? 0 : 40;
            const minHeight = this.state.isKeyboardOpen ? this.config.minKeyboardMessagesHeightPx : this.config.minMessagesHeightPx;
            let availableHeight = 0;

            if (this.state.isKeyboardOpen && $chatInput.length) {
                const areaRect = $messagesArea.get(0).getBoundingClientRect();
                const inputRect = $chatInput.get(0).getBoundingClientRect();
                const inputTop = Math.floor(inputRect.top || 0);
                const areaTop = Math.max(0, Math.floor(areaRect.top || 0));
                availableHeight = inputTop - areaTop;

                if (!availableHeight || availableHeight < minHeight) {
                    const visualBottom = this.getVisualViewportBottom();
                    availableHeight = Math.floor(visualBottom - areaTop - inputHeight);
                }
            } else {
                const navbarHeight = window.innerWidth >= 992
                    ? ($('.navbar').first().outerHeight(true) || 0)
                    : 0;

                availableHeight = usableBodyHeight - footerHeight - profileHeight - inputHeight - navbarHeight - compactReserve - safeAreaReserve;

                if (!availableHeight || availableHeight < minHeight) {
                    availableHeight = viewportHeight - footerHeight - profileHeight - inputHeight - navbarHeight - compactReserve - safeAreaReserve;
                }
            }

            availableHeight = Math.max(Math.floor(availableHeight), minHeight);
            this.state.baseMessagesHeight = availableHeight;

            $messagesArea.css({
                height: `${availableHeight}px`,
                maxHeight: `${availableHeight}px`,
                paddingBottom: this.state.isKeyboardOpen ? "8px" : ""
            });

            if (shouldStickToBottom) {
                this.scrollToBottom(this.state.isKeyboardOpen);
            }

            this.updateActiveDateBadge();
        },

        scheduleHeightAdjust: function (forceScrollToBottom) {
            const self = this;
            if (self.state.resizeTimer) window.clearTimeout(self.state.resizeTimer);

            self.state.resizeTimer = window.setTimeout(() => {
                self.state.resizeTimer = null;
                self.adjustHeight(forceScrollToBottom);
            }, self.state.isKeyboardOpen ? self.config.keyboardAdjustDelayMs : 80);
        },

        onTextareaFocus: function () {
            if (!this.state.keyboardViewportAnchor) this.captureKeyboardViewportAnchor();

            this.state.keyboardBaselineHeight = Math.max(this.state.keyboardBaselineHeight || 0, window.innerHeight || $(window).height() || 0);
            this.state.keyboardFocusStartedAt = Date.now();
            document.body.classList.add("chat-keyboard-transitioning", "chat-keyboard-open");
            this.state.isKeyboardOpen = true;
            this.closeMessageActionPopover();

            const shouldStickToBottom = this.isNearBottom();
            this.requestKeyboardLayoutUpdate(shouldStickToBottom);
            this.scheduleKeyboardSettleUpdates(shouldStickToBottom);

            if (this.state.keyboardFocusTimer) window.clearTimeout(this.state.keyboardFocusTimer);
            this.state.keyboardFocusTimer = window.setTimeout(() => {
                this.requestKeyboardLayoutUpdate(true);
                window.setTimeout(() => document.body.classList.remove("chat-keyboard-transitioning"), this.config.keyboardAnimationMs + 80);
            }, 320);
        },

        onTextareaBlur: function () {
            if (this.state.keyboardFocusTimer) {
                window.clearTimeout(this.state.keyboardFocusTimer);
                this.state.keyboardFocusTimer = null;
            }
            this.clearKeyboardSettleTimers();
            document.body.classList.add("chat-keyboard-transitioning");
            window.setTimeout(() => {
                const textareaFocused = document.activeElement && document.activeElement.id === "messageText";
                if (textareaFocused) return;
                this.resetKeyboardLayout();
                this.scheduleHeightAdjust(false);
                window.setTimeout(() => document.body.classList.remove("chat-keyboard-transitioning"), this.config.keyboardAnimationMs + 80);
            }, 80);
        },

        bindViewportEvents: function () {
            const self = this;
            self.unbindViewportEvents();

            self.state.viewportResizeHandler = function () {
                self.requestKeyboardLayoutUpdate(self.state.isKeyboardOpen && self.isNearBottom());
            };
            self.state.viewportScrollHandler = function () {
                if (!self.state.viewportLayoutManaged && !self.state.isKeyboardOpen) return;
                self.requestKeyboardLayoutUpdate(false);
            };

            if (window.visualViewport) {
                window.visualViewport.addEventListener("resize", self.state.viewportResizeHandler, { passive: true });
                window.visualViewport.addEventListener("scroll", self.state.viewportScrollHandler, { passive: true });
            }

            if (navigator.virtualKeyboard) {
                try {
                    if (self.state.viewportLayoutManaged) {
                        // Yalnızca sohbet görünümlerinde varsayılan resize davranışını koru.
                        // Overlay bazı Android WebView sürümlerinde viewport küçülmesini engelliyor.
                        self.state.virtualKeyboardOverlayOriginal = !!navigator.virtualKeyboard.overlaysContent;
                        navigator.virtualKeyboard.overlaysContent = false;
                    } else {
                        // Diğer sohbet görünümlerinin mevcut davranışını değiştirme.
                        navigator.virtualKeyboard.overlaysContent = true;
                    }
                } catch (error) {
                    // Salt-okunur/deneysel uygulamalarda varsayılan davranış korunur.
                }

                try {
                    self.state.virtualKeyboardGeometryHandler = function () {
                        self.requestKeyboardLayoutUpdate(self.state.isKeyboardOpen && self.isNearBottom());
                    };
                    navigator.virtualKeyboard.addEventListener("geometrychange", self.state.virtualKeyboardGeometryHandler);
                } catch (error) {
                    self.state.virtualKeyboardGeometryHandler = null;
                }
            }
        },

        unbindViewportEvents: function () {
            if (window.visualViewport) {
                if (this.state.viewportResizeHandler) window.visualViewport.removeEventListener("resize", this.state.viewportResizeHandler);
                if (this.state.viewportScrollHandler) window.visualViewport.removeEventListener("scroll", this.state.viewportScrollHandler);
            }
            if (navigator.virtualKeyboard && this.state.virtualKeyboardGeometryHandler) {
                try {
                    navigator.virtualKeyboard.removeEventListener("geometrychange", this.state.virtualKeyboardGeometryHandler);
                } catch (error) {
                    // Bazı eski Android WebView sürümleri removeEventListener çağrısını desteklemiyor.
                }
            }
            if (navigator.virtualKeyboard && this.state.virtualKeyboardOverlayOriginal !== null) {
                try {
                    navigator.virtualKeyboard.overlaysContent = this.state.virtualKeyboardOverlayOriginal;
                } catch (error) {
                    // Salt-okunur/deneysel uygulamalarda mevcut değer korunur.
                }
            }
            this.state.viewportResizeHandler = null;
            this.state.viewportScrollHandler = null;
            this.state.virtualKeyboardGeometryHandler = null;
            this.state.virtualKeyboardOverlayOriginal = null;
        },

        requestKeyboardLayoutUpdate: function (forceScrollToBottom) {
            if (this.state.keyboardLayoutFrame) window.cancelAnimationFrame(this.state.keyboardLayoutFrame);
            this.state.keyboardLayoutFrame = window.requestAnimationFrame(() => {
                this.state.keyboardLayoutFrame = null;
                this.updateKeyboardLayout();
                this.adjustHeight(!!forceScrollToBottom);
            });
        },

        updateKeyboardLayout: function () {
            const $chatInput = $(this.config.chatInputSelector).first();
            const viewport = window.visualViewport;
            const layoutHeight = window.innerHeight || $(window).height() || 0;
            const visualHeight = viewport && viewport.height ? viewport.height : layoutHeight;
            const visualOffsetTop = viewport && viewport.offsetTop ? viewport.offsetTop : 0;
            const visualBottom = Math.round(visualOffsetTop + visualHeight);
            const virtualKeyboardRect = navigator.virtualKeyboard && navigator.virtualKeyboard.boundingRect
                ? navigator.virtualKeyboard.boundingRect
                : null;
            const virtualKeyboardHeight = virtualKeyboardRect
                ? Math.max(0, Math.round(virtualKeyboardRect.height || 0))
                : 0;
            let keyboardInset = 0;

            if (viewport && viewport.height) {
                keyboardInset = Math.max(0, Math.round(layoutHeight - visualHeight - visualOffsetTop));
            }
            if (this.state.keyboardBaselineHeight) {
                keyboardInset = Math.max(
                    keyboardInset,
                    Math.max(0, Math.round(this.state.keyboardBaselineHeight - layoutHeight))
                );
            }

            // Android WebView klavyeyi içerik üzerine bindirdiğinde visualViewport küçülmez.
            // VirtualKeyboard geometrisi bu durumda giriş alanının gerçek kapladığı bölgeyi verir.
            keyboardInset = Math.max(keyboardInset, virtualKeyboardHeight);

            const textareaFocused = document.activeElement && document.activeElement.id === "messageText";
            const focusGraceActive = textareaFocused && this.state.keyboardFocusStartedAt > 0 &&
                (Date.now() - this.state.keyboardFocusStartedAt) < 900;
            const keyboardOpen = textareaFocused &&
                (keyboardInset > this.config.keyboardInsetThresholdPx || focusGraceActive);

            this.state.keyboardInsetPx = keyboardOpen ? keyboardInset : 0;
            this.state.isKeyboardOpen = !!keyboardOpen;
            document.body.classList.toggle("chat-keyboard-open", !!keyboardOpen);
            document.documentElement.style.setProperty("--chat-viewport-height", `${Math.floor(visualHeight)}px`);

            if (!$chatInput.length) return;

            if (this.state.viewportLayoutManaged) {
                $chatInput.css({ left: "", width: "", bottom: "" });
                document.documentElement.style.removeProperty("--chat-keyboard-bottom");
                document.documentElement.style.removeProperty("--chat-input-left");
                document.documentElement.style.removeProperty("--chat-input-width");
                this.updateManagedViewportLayout(virtualKeyboardRect);
                return;
            }

            if (!keyboardOpen) {
                $chatInput.css({ left: "", width: "", bottom: "" });
                document.documentElement.style.removeProperty("--chat-keyboard-bottom");
                document.documentElement.style.removeProperty("--chat-input-left");
                document.documentElement.style.removeProperty("--chat-input-width");
                document.documentElement.style.removeProperty("--chat-viewport-height");
                return;
            }

            const $messagesArea = this.getMessagesArea();
            const baseRect = $messagesArea.length ? $messagesArea.get(0).getBoundingClientRect() : $chatInput.get(0).getBoundingClientRect();
            const left = Math.max(0, Math.round(baseRect.left || 0));
            const width = Math.max(0, Math.round(baseRect.width || window.innerWidth || 0));

            document.documentElement.style.setProperty("--chat-input-left", `${left}px`);
            document.documentElement.style.setProperty("--chat-input-width", `${width}px`);
            document.documentElement.style.setProperty("--chat-keyboard-bottom", "0px");
            $chatInput.css({ left: `${left}px`, width: `${width}px`, bottom: "0px" });

            const inputRect = $chatInput.get(0).getBoundingClientRect();
            const hiddenAmount = Math.max(0, Math.round((inputRect.bottom || layoutHeight) - visualBottom));
            const viewportWasNotResized = Math.abs(layoutHeight - visualHeight) <= this.config.keyboardInsetThresholdPx;
            const overlayBottom = viewportWasNotResized ? virtualKeyboardHeight : 0;
            const bottom = Math.max(hiddenAmount > 6 ? hiddenAmount + 2 : 0, overlayBottom);

            document.documentElement.style.setProperty("--chat-keyboard-bottom", `${bottom}px`);
            $chatInput.css({ bottom: `${bottom}px` });

            const correctedRect = $chatInput.get(0).getBoundingClientRect();
            if (correctedRect.bottom > (visualBottom - 2) && overlayBottom === 0) {
                const correction = Math.ceil(correctedRect.bottom - visualBottom + 2);
                document.documentElement.style.setProperty("--chat-keyboard-bottom", `${bottom + correction}px`);
                $chatInput.css({ bottom: `${bottom + correction}px` });
            }
        },

        getVisualViewportBottom: function () {
            const viewport = window.visualViewport;
            if (viewport && viewport.height) return Math.round((viewport.offsetTop || 0) + viewport.height);
            return Math.round(window.innerHeight || $(window).height() || 0);
        },

        resetKeyboardLayout: function () {

            try {

                this.state.isKeyboardOpen = false;
                this.state.keyboardInsetPx = 0;
                this.state.keyboardFocusStartedAt = 0;
                if (this.state.keyboardLayoutFrame) { window.cancelAnimationFrame(this.state.keyboardLayoutFrame); this.state.keyboardLayoutFrame = null; }
                document.body.classList.remove("chat-keyboard-open", "chat-keyboard-transitioning");
                this.clearKeyboardSettleTimers();
                this.clearManagedViewportKeyboardLock(true);
                document.documentElement.style.removeProperty("--chat-keyboard-bottom");
                document.documentElement.style.removeProperty("--chat-input-left");
                document.documentElement.style.removeProperty("--chat-input-width");
                document.documentElement.style.removeProperty("--chat-viewport-height");
                $(this.config.chatInputSelector).first().css({ left: "", width: "", bottom: "" });
                this.getMessagesArea().css({ paddingBottom: "" });
            } catch (e) {

                // Sanal klavye bu cihazda desteklenmiyor.
                // console.error(e);
            }
        },

        buildMessagesHtml: function (messages, previousDateKey) {
            let html = "";
            let lastDateKey = previousDateKey || null;

            for (const message of messages) {
                const dateKey = this.getMessageDateKey(message);
                if (dateKey && dateKey !== lastDateKey) {
                    html += this.renderDateBadge(dateKey);
                    lastDateKey = dateKey;
                }
                html += this.buildMessageHtml(message);
            }
            return html;
        },

        buildMessageHtml: function (message) {
            const isSystemNotification = this.isSystemNotification(message);
            const memberId = isSystemNotification ? "system" : this.getValue(message, ["MemberId", "memberId"]);
            const isMine = !isSystemNotification && parseInt(memberId, 10) === parseInt(this.state.currentUserId, 10);
            const rawSendDate = this.getValue(message, ["SendDate", "sendDate"]);
            const sendDate = this.parseDate(rawSendDate);
            const timeText = sendDate ? this.formatTime(sendDate) : "";
            const dateKey = sendDate ? this.formatDateKey(sendDate) : "";
            const deleteExpiresAt = sendDate ? new Date(sendDate.getTime() + (this.config.deleteWindowMinutes * 60000)) : null;
            const actionId = this.getValue(message, ["ActionId", "actionId"]);
            const actionTypeId = this.getValue(message, ["ActionTypeId", "actionTypeId"]);
            const objectId = this.getValue(message, ["ObjectId", "objectId"]);
            const objectTypeId = this.getValue(message, ["ObjectTypeId", "objectTypeId"]);
            const isChangeLog = isSystemNotification && parseInt(objectTypeId, 10) === this.config.changelogObjectTypeId && parseInt(objectId, 10) > 0;
            const isActionCard = !isSystemNotification && parseInt(actionId, 10) > 0;
            const isObjectCard = !isSystemNotification && !isActionCard && parseInt(objectId, 10) > 0;
            const rawMemberName = this.getValue(message, ["MemberName", "memberName"]) || "-";
            const memberName = isMine ? "Siz" : this.safeText(rawMemberName);
            const memberImage = this.safeAttr(this.getValue(message, ["MemberImg", "memberImg", "MemberImage", "memberImage"]) || "https://sahasenin.com/avatar.webp");
            const messageId = this.getMessageId(message);
            const isReported = this.isMessageReported(message);
            const isEdited = this.isMessageEdited(message);
            const hasImage = this.messageHasImage(message);
            const hasVideo = this.messageHasVideo(message);
            const hasFile = this.messageHasFile(message);
            const isForwarded = !!this.getValue(message, ["IsForwarded", "isForwarded"]);

            const itemClasses = ["chat-message-item"];
            if (isSystemNotification) itemClasses.push("is-system-notification");
            if (isChangeLog) itemClasses.push("is-changelog-card");
            else itemClasses.push(isMine ? "is-mine" : "is-other");
            if (isActionCard) itemClasses.push("is-action-card");
            if (isObjectCard) itemClasses.push("is-object-card");
            if (isReported) itemClasses.push("is-reported");
            if (isEdited) itemClasses.push("is-edited");
            if (isForwarded) itemClasses.push("is-forwarded");

            const content = isSystemNotification
                ? (isChangeLog ? this.renderChangeLogCard(message, timeText, objectId) : this.renderSystemNotification(message, timeText))
                : (isActionCard
                    ? this.renderActionCard(actionId, actionTypeId, memberName, memberImage, timeText, isMine)
                    : (isObjectCard
                        ? this.renderObjectCard(objectId, objectTypeId, memberName, memberImage, timeText, isMine)
                        : this.renderRegularMessage(message, isMine, memberName, memberImage, timeText, dateKey, memberId, isReported, isEdited)));

            return `<div class="${itemClasses.join(" ")}" data-id="${this.safeAttr(messageId)}" data-date="${this.safeAttr(dateKey)}" data-time="${this.safeAttr(timeText)}" data-send-date="${this.safeAttr(rawSendDate || "")}" data-delete-expires-at="${this.safeAttr(deleteExpiresAt ? deleteExpiresAt.toISOString() : "")}" data-member="${this.safeAttr(memberId || "")}" data-system="${isSystemNotification ? "true" : "false"}" data-is-mine="${isMine ? "true" : "false"}" data-action-id="${this.safeAttr(actionId || 0)}" data-action-type="${this.safeAttr(actionTypeId || 0)}" data-object-id="${this.safeAttr(objectId || 0)}" data-is-structured="${(isActionCard || isObjectCard) ? "true" : "false"}" data-reported="${isReported ? "true" : "false"}" data-edited="${isEdited ? "true" : "false"}" data-has-image="${hasImage ? "true" : "false"}" data-has-video="${hasVideo ? "true" : "false"}" data-has-file="${hasFile ? "true" : "false"}" data-is-forwarded="${isForwarded ? "true" : "false"}">${content}</div>`;
        },

        renderSystemNotification: function (message, timeText) {
            const messageText = this.formatMessageText(this.getValue(message, ["Message", "message"]) || "");
            if (!messageText) return "";

            const timeHtml = timeText
                ? `<span class="chat-system-notification-time text-muted small ms-1 fs-06">${this.safeText(timeText)}</span>`
                : "";

            return `
                <div class="chat-system-notification text-center my-2 px-2" style="width:100%;" role="status" aria-live="polite">
                    <span class="chat-system-notification-text text-muted small fs-07">${messageText}</span>${timeHtml}
                </div>
            `;
        },

        renderChangeLogCard: function (message, timeText, changeLogId) {
            const rawTitle = this.getValue(message, ["Message", "message"]) || "Yeni güncelleme";
            const title = this.safeText(rawTitle);
            const image = this.extractMediaUrl(this.getValue(message, ["Image", "image"]));
            const imageHtml = image ? `<img class="chat-changelog-card__image" src="${this.safeAttr(image)}" alt="" loading="lazy">` : "";
            return `<button type="button" class="chat-changelog-card" data-changelog-card data-changelog-id="${this.safeAttr(changeLogId)}" aria-label="Versiyon yönetiminü aç: ${this.safeAttr(rawTitle)}">
                ${imageHtml}<span class="chat-changelog-card__body"><span class="chat-changelog-card__eyebrow">SAHASENİN'DE YENİ</span>
                <span class="chat-changelog-card__title">${title}</span><span class="chat-changelog-card__foot"><span>Güncellemeleri keşfet <i class="fa-solid fa-arrow-right" aria-hidden="true"></i></span><span class="chat-changelog-card__time">${this.safeText(timeText)}</span></span></span></button>`;
        },

        renderRegularMessage: function (message, isMine, memberName, memberImage, timeText, dateKey, memberId, isReported, isEdited) {
            const messageText = this.formatMessageText(this.getValue(message, ["Message", "message"]) || "");
            const fileUrl = this.extractMediaUrl(this.getValue(message, ["File", "file", "FileUrl", "fileUrl"]));
            const imageUrl = this.extractMediaUrl(this.getValue(message, ["Image", "image", "ImageUrl", "imageUrl"]));
            const videoUrl = this.extractMediaUrl(this.getValue(message, ["Video", "video", "VideoUrl", "videoUrl"]));
            const rawNickName = this.getValue(message, ["MemberNickName", "memberNickName"]) || "";
            const profileUrl = !isMine && rawNickName ? `/Oyuncu-Bul/${encodeURIComponent(String(rawNickName))}` : "";
            const metaClasses = ["chat-message-meta", isMine ? "is-meta-mine" : "is-meta-other"];
            if (!isMine && rawNickName) metaClasses.push("cardOpenSheepPopup");
            const metaAttrs = !isMine && rawNickName ? ` data-url="${this.safeAttr(profileUrl)}"` : "";
            const forwardedHtml = this.getValue(message, ["IsForwarded", "isForwarded"]) ? '<div class="chat-forwarded-inline-wrap"><span class="chat-forwarded-label fs-06"><i class="fa fa-share"></i> İletildi</span></div>' : "";
            const reportedHtml = isReported
                ? `<div class="chat-reported-badge fs-06" aria-label="Bu mesaj şikayet edildi"><i class="fa fa-flag"></i><span class="${isMine ? "text-white" : "text-red"}">Şikayet Edildi</span></div>`
                : "";
            const editedInlineHtml = isEdited
                ? `<div class="chat-edited-inline-wrap"><span class="chat-edited-label chat-edited-inline-label fs-06" aria-label="Düzenlenmiş mesaj"><i class="fa fa-pen"></i> düzenlendi</span></div>`
                : "";

            let innerHtml = `
                <div class="chat-bubble" data-date="${this.safeAttr(dateKey)}" data-member="${this.safeAttr(memberId || "")}" tabindex="0" role="button" aria-label="Mesaj işlemleri">
                    <div class="chat-message-content">
                        ${reportedHtml}
                        ${forwardedHtml}
            `;

            if (imageUrl) {
                innerHtml += `
                        <div class="chat-image-box openImage" data-url="${this.safeAttr(imageUrl)}" role="button" tabindex="0" aria-label="Resmi aç">
                            <img src="${this.safeAttr(imageUrl)}" class="chat-message-image" alt="resim" />
                        </div>
                `;
            }

            if (videoUrl) {
                innerHtml += `
                        <div class="chat-video-box" role="group" aria-label="Video mesajı">
                            <video class="chat-message-video" controls preload="metadata" playsinline>
                                <source src="${this.safeAttr(videoUrl)}">
                                Tarayıcınız video oynatmayı desteklemiyor.
                            </video>
                        </div>
                `;
            }

            if (messageText) {
                innerHtml += `
                        <div class="chat-text-wrap">
                            <p class="chat-text fs-08">${messageText}</p>
                        </div>
                `;
            }

            if (fileUrl) {
                innerHtml += `<a class="chat-file-link fs-07" href="${this.safeAttr(fileUrl)}" target="_blank" rel="noopener noreferrer"><i class="fa fa-file"></i><span>Dosya indir</span></a>`;
            }

            innerHtml += editedInlineHtml;

            innerHtml += `
                    </div>
                    <div class="${metaClasses.join(" ")}"${metaAttrs}>
                        <span class="chat-meta-member">
                            <img src="${memberImage}" class="chat-avatar" alt="${memberName}">
                            <span class="chat-member-name fs-06">${memberName}</span>
                        </span>
                        <span class="chat-time fs-06">${this.safeText(timeText)}</span>
                    </div>
                </div>
                <span class="chat-reveal-time fs-06" aria-hidden="true">${this.safeText(timeText)}</span>
            `;

            return innerHtml;
        },

        bindMessageActionEvents: function () {
            const self = this;
            self.unbindMessageActionEvents();

            const startSelector = `${self.config.messagesAreaSelector} .chat-message-item:not(.is-system-notification):not(.is-action-card):not(.is-object-card) .chat-bubble, ${self.config.messagesAreaFallbackSelector} .chat-message-item:not(.is-system-notification):not(.is-action-card):not(.is-object-card) .chat-bubble, ${self.config.messagesAreaSelector} .chat-message-item.is-expired-reservation-announcement .chat-structured-message-wrap, ${self.config.messagesAreaFallbackSelector} .chat-message-item.is-expired-reservation-announcement .chat-structured-message-wrap`;

            $(document)
                .on("pointerdown.chatMessageActions", startSelector, function (event) { self.onMessageActionStart(event, $(this)); })
                .on("pointermove.chatMessageActions", function (event) { self.onMessageActionMove(event); })
                .on("pointerup.chatMessageActions pointercancel.chatMessageActions", function () { self.cancelMessageActionTimer(); })
                .on("contextmenu.chatMessageActions", startSelector, function (event) {
                    event.preventDefault();
                    self.showMessageActionPopover($(this).closest(".chat-message-item"));
                })
                .on("click.chatMessageActions", "[data-chat-copy-message]", function (event) {
                    event.preventDefault();
                    self.copyMessageText($(this).data("chat-copy-message"), $(this));
                })
                .on("click.chatMessageActions", "[data-chat-edit-message]", function (event) {
                    event.preventDefault();
                    self.startComposerEdit($(this).data("chat-edit-message"));
                })
                .on("click.chatMessageActions", "[data-chat-report-message]", function (event) {
                    event.preventDefault();
                    self.reportMessage($(this).data("chat-report-message"));
                })
                .on("click.chatMessageActions", "[data-chat-withdraw-report]", function (event) {
                    event.preventDefault();
                    self.withdrawMessageReport($(this).data("chat-withdraw-report"));
                })
                .on("click.chatMessageActions", "[data-chat-delete-message]", function (event) {
                    event.preventDefault();
                    self.deleteMessageForEveryone($(this).data("chat-delete-message"));
                })
                .on("click.chatMessageActions", "[data-chat-forward-message]", function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    self.startForwardMessage($(this).data("chat-forward-message"));
                    return false;
                })
                .on("click.chatMessageActions", "[data-chat-forward-chat]", function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    self.forwardMessageToChat($(this).data("chat-forward-chat"));
                    return false;
                })
                .on("click.chatMessageActions", "[data-chat-close-message-actions]", function (event) {
                    event.preventDefault();
                    self.closeMessageActionPopover();
                })
                .on("click.chatMessageActions", function (event) {
                    const $target = $(event.target);
                    if ($target.closest(".chat-message-action-popover").length > 0) return;

                    const activeMessageId = parseInt(self.state.activeMessageActionId, 10) || 0;
                    const $activeItem = activeMessageId > 0 ? self.getMessageItem(activeMessageId) : $();
                    const activeActionTypeId = parseInt(
                        $activeItem.attr("data-action-type") || $activeItem.data("action-type") || 0,
                        10
                    ) || 0;
                    const clickedMessageSurface = $target.closest(".chat-message-item .chat-bubble, .chat-message-item.is-expired-reservation-announcement .chat-structured-message-wrap").length > 0;

                    // Rezervasyon duyurusunun okundu bilgisi, başka bir mesaj veya
                    // sayfadaki herhangi bir alan tıklanınca mutlaka kapanmalıdır.
                    if (activeActionTypeId === self.config.reservationAnnouncementActionTypeId) {
                        self.closeMessageActionPopover();
                        return;
                    }

                    if ($target.closest(".OpenSheepPopup, .cardOpenSheepPopup, .openImage, #sharePopup").length === 0 && !clickedMessageSurface) {
                        self.closeMessageActionPopover();
                    }
                });

            $(window)
                .off("resize.chatMessageActions scroll.chatMessageActions")
                .on("resize.chatMessageActions scroll.chatMessageActions", throttle(function () {
                    self.repositionActiveMessageActionPopover();
                }, 80));
        },

        unbindMessageActionEvents: function () {
            $(document).off(".chatMessageActions");
            $(window).off(".chatMessageActions");
        },

        onMessageActionStart: function (event, $bubble) {
            if (!$bubble || $bubble.length === 0) return;
            if ($(event.target).closest("a, button, input, textarea, select, .chat-file-link, .chat-image-box").length > 0) return;

            const $item = $bubble.closest(".chat-message-item");
            if (!this.canOpenMessageInfoForItem($item)) return;
            const messageId = parseInt($item.data("id"), 10) || 0;
            if (messageId <= 0) return;

            this.cancelMessageActionTimer();
            $item.addClass("is-pressing");
            this.state.messageActionPointerId = event.pointerId !== undefined ? event.pointerId : null;
            this.state.messageActionStartX = event.clientX || 0;
            this.state.messageActionStartY = event.clientY || 0;

            this.state.messageActionTimer = window.setTimeout(() => {
                this.state.messageActionTimer = null;
                $item.removeClass("is-pressing").addClass("is-long-press-fired");
                window.setTimeout(() => $item.removeClass("is-long-press-fired"), 260);
                this.showMessageActionPopover($item);
            }, this.config.longPressDelayMs);
        },

        onMessageActionMove: function (event) {
            if (!this.state.messageActionTimer) return;
            if (this.state.messageActionPointerId !== null && event.pointerId !== this.state.messageActionPointerId) return;

            const dx = Math.abs((event.clientX || 0) - this.state.messageActionStartX);
            const dy = Math.abs((event.clientY || 0) - this.state.messageActionStartY);
            if (dx > this.config.longPressMoveTolerancePx || dy > this.config.longPressMoveTolerancePx) {
                this.cancelMessageActionTimer();
            }
        },

        cancelMessageActionTimer: function () {
            if (this.state.messageActionTimer) {
                window.clearTimeout(this.state.messageActionTimer);
                this.state.messageActionTimer = null;
            }
            this.state.messageActionPointerId = null;
            this.getMessagesArea().find(".chat-message-item.is-pressing").removeClass("is-pressing");
        },

        showMessageActionPopover: function ($item) {
            if (!$item || $item.length === 0 || !this.canOpenMessageInfoForItem($item)) return;

            const messageId = parseInt($item.data("id"), 10) || 0;
            if (messageId <= 0) return;

            this.closeMessageActionPopover(false);
            this.state.activeMessageActionId = messageId;
            $item.removeClass("is-pressing").addClass("is-menu-open");
            $item.append(this.renderMessageActionLoading(messageId));
            this.positionMessageActionPopover($item);
            this.loadMessageInfo(messageId);
        },

        closeMessageActionPopover: function (resetActive) {
            this.cancelMessageActionTimer();
            this.getMessagesArea()
                .find(".chat-message-action-popover")
                .remove();
            this.getMessagesArea()
                .find(".chat-message-item.is-menu-open")
                .removeClass("is-menu-open");

            if (resetActive !== false) this.state.activeMessageActionId = null;
        },

        renderMessageActionLoading: function (messageId) {
            return `
                <div class="chat-message-action-popover is-loading" data-message-info-popover data-message-id="${this.safeAttr(messageId)}" role="dialog" aria-label="Mesaj bilgisi">
                    <button type="button" class="chat-message-action-close" data-chat-close-message-actions aria-label="Kapat"><i class="fa fa-times"></i></button>
                    <div class="chat-message-action-title fs-08">Mesaj Bilgisi</div>
                    <div class="chat-message-action-loader fs-07">Bilgiler yükleniyor...</div>
                </div>
            `;
        },

        loadMessageInfo: function (messageId) {
            const self = this;
            self.state.isMessageInfoLoading = true;

            $.ajax({
                url: self.config.messageInfoAction,
                type: "POST",
                data: { id: messageId },
                success: function (response) {
                    if (parseInt(self.state.activeMessageActionId, 10) !== parseInt(messageId, 10)) return;

                    const payload = self.normalizeMessageInfoResponse(response);
                    if (!payload.success) {
                        self.renderMessageInfoError(messageId, payload.message || "Mesaj bilgisi alınamadı.");
                        return;
                    }

                    self.renderMessageInfo(messageId, payload);
                },
                error: function (xhr) {
                    self.renderMessageInfoError(messageId, self.getErrorMessage(xhr, "Mesaj bilgisi alınamadı."));
                },
                complete: function () { self.state.isMessageInfoLoading = false; }
            });
        },

        renderMessageInfoError: function (messageId, message) {
            const $popover = this.getMessageActionPopover(messageId);
            if ($popover.length === 0) return;

            $popover
                .removeClass("is-loading")
                .html(`
                    <button type="button" class="chat-message-action-close" data-chat-close-message-actions aria-label="Kapat"><i class="fa fa-times"></i></button>
                    <div class="chat-message-action-title fs-08">Mesaj Bilgisi</div>
                    <div class="chat-message-action-error fs-07">${this.safeText(message)}</div>
                `);

            this.positionMessageActionPopover(this.getMessageItem(messageId));
        },

        renderMessageInfo: function (messageId, payload) {
            const $popover = this.getMessageActionPopover(messageId);
            if ($popover.length === 0) return;

            if (payload && (payload.currentUserIsChatAdmin !== undefined || payload.CurrentUserIsChatAdmin !== undefined)) {
                this.state.currentUserIsChatAdmin = this.normalizeBoolean(
                    payload.currentUserIsChatAdmin !== undefined ? payload.currentUserIsChatAdmin : payload.CurrentUserIsChatAdmin
                );
            }

            const isMine = !!payload.isMine;
            const reported = !!payload.reported;
            const isExpiredReservationAnnouncement = !!payload.isExpiredReservationAnnouncement;
            const title = isExpiredReservationAnnouncement ? "Duyuru Bilgisi" : (isMine ? "Mesaj Bilgisi" : "Mesaj İşlemleri");
            const reportedNotice = reported
                ? `<div class="chat-message-reported-notice fs-07"><i class="fa fa-flag"></i><span>Bu mesaj şikayet edildi.</span></div>`
                : "";
            const bodyHtml = isExpiredReservationAnnouncement
                ? this.renderExpiredReservationMessageInfoBody(payload)
                : (isMine
                    ? this.renderOwnMessageInfoBody(messageId, payload)
                    : this.renderOtherMessageInfoBody(messageId, payload));

            $popover
                .removeClass("is-loading")
                .toggleClass("is-reported", reported)
                .html(`
                    <button type="button" class="chat-message-action-close" data-chat-close-message-actions aria-label="Kapat"><i class="fa fa-times"></i></button>
                    <div class="chat-message-action-title fs-08">${this.safeText(title)}</div>
                    ${reportedNotice}
                    ${bodyHtml}
                `);

            this.positionMessageActionPopover(this.getMessageItem(messageId));
            this.syncMessageReportedState(messageId, reported);
        },

        renderExpiredReservationMessageInfoBody: function (payload) {
            const readedMembers = payload.readedMembers || [];
            const unreadedMembers = payload.unreadedMembers || [];

            return `
                <div class="chat-expired-announcement-info fs-07">
                    <i class="fa-solid fa-clock-rotate-left"></i>
                    <span>Süresi dolmuş rezervasyon duyurusu</span>
                </div>
                ${this.renderMessageSenderSection(payload)}
                ${this.renderMessageMemberSection("Görenler", readedMembers, "Henüz gören yok")}
                ${this.renderMessageMemberSection("Henüz Görmeyenler", unreadedMembers, "Herkes gördü")}
            `;
        },

        renderOwnMessageInfoBody: function (messageId, payload) {
            const readedMembers = payload.readedMembers || [];
            const unreadedMembers = payload.unreadedMembers || [];
            const canDelete = this.canDeleteMessageForCurrentUser(payload);
            const canEdit = !!payload.canEditText;
            const reported = !!payload.reported;
            const hasText = !!(payload.messageText || "").trim();
            const canCopy = hasText;
            const deleteReasonText = (this.isCurrentUserChatAdmin()
                ? ""
                : (payload.deleteDisabledReason || (canDelete ? "" : "Silme süresi doldu.")) || "").trim();
            const editReasonText = ((hasText && !canEdit)
                ? (payload.editDisabledReason || "Düzenleme süresi doldu.")
                : "").trim();
            const canWithdrawReport = !!payload.canWithdrawReport;
            const withdrawReasonText = (payload.withdrawReportDisabledReason || "").trim();

            const editButtonHtml = hasText
                ? `<button type="button" class="chat-message-secondary-action fs-07 is-edit-action" data-chat-edit-message="${this.safeAttr(messageId)}" ${canEdit ? "" : "disabled"}>
                        <i class="fa-regular fa-pen-to-square"></i>
                        <span>Düzenle</span>
                   </button>`
                : "";

            const withdrawButtonHtml = reported
                ? `<button type="button" class="chat-withdraw-report fs-07" data-chat-withdraw-report="${this.safeAttr(messageId)}" ${canWithdrawReport ? "" : "disabled"}>
                        <i class="fa-solid fa-rotate-left"></i>
                        <span>Geri Al</span>
                   </button>`
                : "";

            const noteHtml = [
                editReasonText ? `<div class="chat-edit-reason fs-06"><i class="fa-regular fa-clock"></i><span>${this.safeText(editReasonText)}</span></div>` : "",
                deleteReasonText ? `<div class="chat-delete-reason fs-06"><i class="fa-solid fa-circle-info"></i><span>${this.safeText(deleteReasonText)}</span></div>` : "",
                reported && withdrawReasonText ? `<div class="chat-report-reason fs-06"><i class="fa-regular fa-flag"></i><span>${this.safeText(withdrawReasonText)}</span></div>` : ""
            ].filter(Boolean).join("");

            return `
                ${this.renderMessageSenderSection(payload)}
                ${this.renderMessageMemberSection("Görenler", readedMembers, "Henüz gören yok")}
                ${this.renderMessageMemberSection("Henüz Görmeyenler", unreadedMembers, "Herkes gördü")}

                <div class="chat-message-action-footer">
                    <div class="chat-message-action-buttons">
                        <button type="button" class="chat-message-secondary-action fs-07 is-copy-action" data-chat-copy-message="${this.safeAttr(payload.messageText || "")}" ${canCopy ? "" : "disabled"}>
                            <i class="fa-regular fa-copy"></i>
                            <span>Kopyala</span>
                        </button>

                        <button type="button" class="chat-message-secondary-action fs-07 is-forward-action" data-chat-forward-message="${this.safeAttr(messageId)}">
                            <i class="fa-solid fa-share"></i>
                            <span>İlet</span>
                        </button>

                        ${editButtonHtml}
                        ${withdrawButtonHtml}

                        <button type="button" class="chat-delete-for-everyone fs-07" data-chat-delete-message="${this.safeAttr(messageId)}" ${canDelete ? "" : "disabled"}>
                            <i class="fa-regular fa-trash-can"></i>
                            <span>Sil</span>
                        </button>
                    </div>

                    ${noteHtml ? `<div class="chat-message-action-notes">${noteHtml}</div>` : ""}
                </div>
            `;
        },

        renderOtherMessageInfoBody: function (messageId, payload) {
            const reported = !!payload.reported;
            const canReport = !!payload.canReport;
            const hasText = !!(payload.messageText || "").trim();
            const canDelete = this.canDeleteMessageForCurrentUser(payload);
            const reportText = payload.canWithdrawReport ? "Şikayetli" : "Şikayet et";
            const reportReasonText = (payload.reportDisabledReason || (reported ? "Bu mesaj şikayet edildi." : "") || "").trim();
            const canWithdrawReport = !!payload.canWithdrawReport;
            const withdrawReasonText = (payload.withdrawReportDisabledReason || "").trim();
            const deleteReasonText = (this.isCurrentUserChatAdmin()
                ? ""
                : (payload.deleteDisabledReason || (canDelete ? "" : "Silme süresi doldu.")) || "").trim();

            const deleteHtml = this.isCurrentUserChatAdmin()
                ? `<button type="button" class="chat-delete-for-everyone fs-07" data-chat-delete-message="${this.safeAttr(messageId)}" ${canDelete ? "" : "disabled"}>
                        <i class="fa-regular fa-trash-can"></i>
                        <span>Sil</span>
                   </button>`
                : "";

            const reportActionHtml = canWithdrawReport
                ? `<button type="button" class="chat-withdraw-report fs-07" data-chat-withdraw-report="${this.safeAttr(messageId)}" ${canWithdrawReport ? "" : "disabled"}>
                        <i class="fa-solid fa-rotate-left"></i>
                        <span>Geri Al</span>
                   </button>`
                : `<button type="button" class="chat-report-message fs-07" data-chat-report-message="${this.safeAttr(messageId)}" ${canReport ? "" : "disabled"}>
                        <i class="fa-regular fa-flag"></i>
                        <span>${this.safeText(reportText)}</span>
                   </button>`;

            const noteHtml = [
                deleteReasonText ? `<div class="chat-delete-reason fs-06"><i class="fa-solid fa-circle-info"></i><span>${this.safeText(deleteReasonText)}</span></div>` : "",
                reported && withdrawReasonText ? `<div class="chat-report-reason fs-06"><i class="fa-regular fa-flag"></i><span>${this.safeText(withdrawReasonText)}</span></div>` : "",
                !reported && reportReasonText ? `<div class="chat-report-reason fs-06"><i class="fa-regular fa-flag"></i><span>${this.safeText(reportReasonText)}</span></div>` : ""
            ].filter(Boolean).join("");

            return `
                ${this.renderMessageSenderSection(payload)}

                <div class="chat-message-action-footer is-report-only">
                    <div class="chat-message-action-buttons">
                        <button type="button" class="chat-message-secondary-action fs-07 is-copy-action" data-chat-copy-message="${this.safeAttr(payload.messageText || "")}" ${hasText ? "" : "disabled"}>
                            <i class="fa-regular fa-copy"></i>
                            <span>Kopyala</span>
                        </button>

                        <button type="button" class="chat-message-secondary-action fs-07 is-forward-action" data-chat-forward-message="${this.safeAttr(messageId)}">
                            <i class="fa-solid fa-share"></i>
                            <span>İlet</span>
                        </button>

                        ${deleteHtml}
                        ${reportActionHtml}
                    </div>

                    ${noteHtml ? `<div class="chat-message-action-notes">${noteHtml}</div>` : ""}
                </div>
            `;
        },

        renderMessageMemberSection: function (title, members, emptyText) {
            const count = members ? members.length : 0;
            const rows = count > 0
                ? members.map(member => this.renderMessageMemberRow(member)).join("")
                : `<div class="chat-message-member-empty fs-07">${this.safeText(emptyText)}</div>`;

            return `
                <div class="chat-message-member-section">
                    <div class="chat-message-member-section-title">
                        <span class="fs-07">${this.safeText(title)}</span>
                        <strong class="fs-06">${count}</strong>
                    </div>
                    <div class="chat-message-member-list fs-07">${rows}</div>
                </div>
            `;
        },

        renderMessageMemberRow: function (member) {
            const img = this.safeAttr(this.getValue(member, ["img", "Img", "memberImg", "MemberImg"]) || "https://sahasenin.com/avatar.webp");
            const name = this.safeText(this.getValue(member, ["fullName", "FullName", "name", "Name"]) || "Kullanıcı");
            const nickNameRaw = this.getValue(member, ["nickName", "NickName"]) || "";
            const nickName = this.safeText(nickNameRaw);
            const nickHtml = nickName ? `<small class="fs-06">@${nickName}</small>` : "";
            const profileUrl = nickNameRaw ? `/Oyuncu-Bul/${encodeURIComponent(String(nickNameRaw))}` : "";
            const popupClass = nickNameRaw ? " cardOpenSheepPopup" : "";
            const popupAttr = nickNameRaw ? ` data-url="${this.safeAttr(profileUrl)}"` : "";

            return `
                <div class="chat-message-member-row${popupClass}"${popupAttr}>
                    <img src="${img}" class="chat-message-member-avatar" alt="${name}">
                    <span class="fs-07">${name}${nickHtml}</span>
                </div>
            `;
        },

        shouldShowMessageSenderSection: function (payload) {
            if (!payload || typeof payload !== "object") return false;

            const isMine = !!this.normalizeBoolean(this.getValue(payload, ["isMine", "IsMine"]));
            if (isMine) return false;

            const chatFlags = [
                this.getValue(payload, ["isPersonalChat", "IsPersonalChat"]),
                this.getValue(payload, ["chatIsPersonal", "ChatIsPersonal"]),
                this.getValue(payload, ["isPrivateChat", "IsPrivateChat"]),
                this.getValue(payload, ["chatIsPrivate", "ChatIsPrivate"]),
                this.getValue(payload, ["isDirectChat", "IsDirectChat"]),
                this.getValue(payload, ["chatType", "ChatType"])
            ];

            if (chatFlags.some(flag => {
                if (flag === null || flag === undefined) return false;
                if (typeof flag === "boolean") return flag;
                const normalized = String(flag).trim().toLowerCase();
                return normalized === "true" || normalized === "1" || normalized === "personal" || normalized === "private" || normalized === "direct";
            })) {
                return false;
            }

            return true;
        },

        renderMessageSenderSection: function (payload) {
            if (!this.shouldShowMessageSenderSection(payload)) return "";

            const memberId = parseInt(this.getValue(payload, ["memberId", "MemberId"]) || 0, 10) || 0;
            const fullName = this.getValue(payload, ["memberName", "MemberName"]) || "Gönderen";
            const nickName = this.getValue(payload, ["memberNickName", "MemberNickName"]) || "";
            const img = this.getValue(payload, ["memberImg", "MemberImg"]) || "https://sahasenin.com/avatar.webp";

            if (memberId <= 0) return "";

            const member = {
                fullName: fullName,
                nickName: nickName,
                img: img
            };

            return `
                <div class="chat-message-member-section is-sender-section">
                    <div class="chat-message-member-section-title">
                        <span class="fs-07">Gönderen</span>
                        <strong class="fs-06">1</strong>
                    </div>
                    <div class="chat-message-member-list fs-07">
                        ${this.renderMessageMemberRow(member)}
                    </div>
                </div>
            `;
        },

        getMessageActionPopover: function (messageId) {
            return this.getMessagesArea().find(`.chat-message-action-popover[data-message-id="${this.escapeJquerySelectorValue(messageId)}"]`).first();
        },

        positionMessageActionPopover: function ($item) {
            if (!$item || $item.length === 0) return;

            const $popover = $item.find(".chat-message-action-popover").first();
            const $bubble = $item.find(".chat-bubble, .chat-structured-message-wrap").first();
            if ($popover.length === 0 || $bubble.length === 0) return;

            const margin = 10;
            const gap = 8;
            const winHeight = window.innerHeight || $(window).height() || 0;
            const rect = $bubble.get(0).getBoundingClientRect();

            $popover
                .removeClass("is-position-top is-position-bottom")
                .css({ maxHeight: "" });

            const naturalHeight = $popover.outerHeight() || 0;
            const spaceAbove = Math.max(0, rect.top - margin - gap);
            const spaceBelow = Math.max(0, winHeight - rect.bottom - margin - gap);
            const placement = (spaceAbove >= naturalHeight || spaceAbove > spaceBelow) ? "top" : "bottom";
            const availableHeight = placement === "top" ? spaceAbove : spaceBelow;
            const maxHeight = Math.max(150, Math.min(naturalHeight || 380, availableHeight - gap));

            $popover
                .addClass("is-position-" + placement)
                .css({ maxHeight: Math.floor(maxHeight) + "px" });
        },

        repositionActiveMessageActionPopover: function () {
            const messageId = parseInt(this.state.activeMessageActionId, 10) || 0;
            if (messageId <= 0) return;
            this.positionMessageActionPopover(this.getMessageItem(messageId));
        },

        copyMessageText: function (text, $button) {
            text = text === undefined || text === null ? "" : String(text);
            if (!text) return;

            const done = () => {
                if (!$button || !$button.length) return;
                const oldHtml = $button.html();
                $button.addClass("is-copied").html('<i class="fa fa-check"></i><span>Kopyalandı</span>');
                window.setTimeout(() => $button.removeClass("is-copied").html(oldHtml), 1100);
            };

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopyText(text, done));
                return;
            }
            this.fallbackCopyText(text, done);
        },

        fallbackCopyText: function (text, done) {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.setAttribute("readonly", "readonly");
            textarea.style.position = "fixed";
            textarea.style.left = "-9999px";
            document.body.appendChild(textarea);
            textarea.select();
            try { document.execCommand("copy"); if (typeof done === "function") done(); }
            catch (e) { this.showError("Mesaj kopyalanamadı."); }
            document.body.removeChild(textarea);
        },

        startForwardMessage: function (messageId) {
            messageId = parseInt(messageId, 10) || 0;
            if (messageId <= 0) return;

            const $item = this.getMessageItem(messageId);
            const $anchor = $item.find(".chat-bubble").first().length ? $item.find(".chat-bubble").first() : $item;

            if (window.sharePopup && typeof window.sharePopup.openMessageForward === "function" && $anchor.length) {
                this.state.forwardMessageId = messageId;
                window.sharePopup.openMessageForward($anchor, {
                    messageId: messageId,
                    currentChatId: this.state.chatId || 0,
                    onSuccess: (response, selectedChatIds) => {
                        const ids = Array.isArray(selectedChatIds) ? selectedChatIds.map(x => parseInt(x, 10) || 0) : [];
                        if (ids.indexOf(parseInt(this.state.chatId, 10) || 0) > -1) {
                            this.syncNewMessages(true);
                        }
                    }
                });
                this.closeMessageActionPopover();
                return;
            }

            this.state.forwardMessageId = messageId;
            this.state.isForwardingMessage = true;
            this.showForwardMessageLoading(messageId);
            this.loadForwardChats(messageId);
        },

        showForwardMessageLoading: function (messageId) {
            const $popover = this.getMessageActionPopover(messageId);
            if ($popover.length === 0) return;

            $popover
                .removeClass("is-loading")
                .html(`
                    <button type="button" class="chat-message-action-close" data-chat-close-message-actions aria-label="Kapat"><i class="fa fa-times"></i></button>
                    <div class="chat-message-action-title fs-08">Mesajı İlet</div>
                    <div class="chat-message-action-loader fs-07">Sohbetler yükleniyor...</div>
                `);
        },

        loadForwardChats: function (messageId) {
            const self = this;
            $.ajax({
                url: "/GetMemberChats",
                type: "POST",
                data: {
                    objectTypeId: 0,
                    chatId: self.state.chatId || 0
                },
                success: function (response) {
                    if (parseInt(self.state.forwardMessageId, 10) !== parseInt(messageId, 10)) return;
                    const chats = self.normalizeForwardChatsResponse(response);
                    self.renderForwardMessageTargets(messageId, chats);
                },
                error: function (xhr) {
                    self.renderForwardMessageError(messageId, self.getErrorMessage(xhr, "Sohbetler yüklenemedi."));
                },
                complete: function () {
                    self.state.isForwardingMessage = false;
                }
            });
        },

        normalizeForwardChatsResponse: function (response) {
            if (!response || typeof response !== "object") return [];
            const list = response.memberChatBasicModels || response.MemberChatBasicModels || response.chatTableModel || response.ChatTableModel || [];
            return Array.isArray(list) ? list : [];
        },

        renderForwardMessageError: function (messageId, message) {
            const $popover = this.getMessageActionPopover(messageId);
            if ($popover.length === 0) return;

            $popover.html(`
                <button type="button" class="chat-message-action-close" data-chat-close-message-actions aria-label="Kapat"><i class="fa fa-times"></i></button>
                <div class="chat-message-action-title fs-08">Mesajı İlet</div>
                <div class="chat-message-action-error fs-07">${this.safeText(message)}</div>
            `);

            this.positionMessageActionPopover(this.getMessageItem(messageId));
        },

        renderForwardMessageTargets: function (messageId, chats) {
            const $popover = this.getMessageActionPopover(messageId);
            if ($popover.length === 0) return;

            const rows = (chats || []).length > 0
                ? chats.map(chat => {
                    const chatId = this.getValue(chat, ["chatId", "ChatId"]) || 0;
                    const title = this.safeText(this.getValue(chat, ["title", "Title"]) || "Sohbet");
                    const img = this.safeAttr(this.getValue(chat, ["img", "Img"]) || "https://sahasenin.com/avatar.webp");
                    return `
                        <div class="chat-message-member-row" data-chat-forward-chat="${this.safeAttr(chatId)}">
                            <img src="${img}" class="chat-message-member-avatar" alt="${title}">
                            <span class="fs-07">${title}</span>
                        </div>
                    `;
                }).join("")
                : '<div class="chat-message-member-empty fs-07">İletilecek sohbet bulunamadı.</div>';

            $popover.html(`
                <button type="button" class="chat-message-action-close" data-chat-close-message-actions aria-label="Kapat"><i class="fa fa-times"></i></button>
                <div class="chat-message-action-title fs-08">Mesajı İlet</div>
                <div class="chat-message-member-section is-sender-section">
                    <div class="chat-message-member-section-title">
                        <span class="fs-07">Sohbetler</span>
                        <strong class="fs-06">${(chats || []).length}</strong>
                    </div>
                    <div class="chat-message-member-list fs-07">${rows}</div>
                </div>
            `);

            this.positionMessageActionPopover(this.getMessageItem(messageId));
        },

        forwardMessageToChat: function (chatId) {
            const messageId = parseInt(this.state.forwardMessageId, 10) || 0;
            chatId = parseInt(chatId, 10) || 0;
            if (messageId <= 0 || chatId <= 0) return;

            const self = this;
            self.state.isForwardingMessage = true;
            $.ajax({
                url: self.config.forwardMessageAction,
                type: "POST",
                data: { messageId: messageId, chatId: chatId },
                success: function (response) {
                    const payload = self.normalizeResponse(response);
                    if (payload.success === false) {
                        self.showError(payload.message || "Mesaj iletilemedi.");
                        return;
                    }
                    self.closeMessageActionPopover();
                    self.state.forwardMessageId = 0;
                    self.syncNewMessages(true);
                },
                error: function (xhr) {
                    if (!xhr.responseJSON?.moderationWarning) self.showError(self.getErrorMessage(xhr, "Mesaj iletilemedi."));
                },
                complete: function () {
                    self.state.isForwardingMessage = false;
                }
            });
        },

        startComposerEdit: function (messageId, messageText) {
            messageId = parseInt(messageId, 10) || 0;
            if (messageId <= 0) return;

            const text = messageText === undefined || messageText === null
                ? this.getMessagePlainText(messageId)
                : String(messageText);

            this.state.editingMessageId = messageId;
            this.state.editingOriginalText = text;
            this.closeMessageActionPopover();
            this.clearSelectedFiles();

            const $form = $(this.config.formSelector).first();
            const $textarea = $(this.config.textareaSelector).first();
            const $sendButton = $form.find(".chat-send-btn").first();

            $form.addClass("chat-form-editing").attr("data-editing-message-id", messageId);
            $textarea.val(text).attr("placeholder", "Mesajı düzenle...");
            this.autoResizeTextarea($textarea.get(0));
            $sendButton.attr("aria-label", "Düzenlemeyi kaydet").html('<i class="fa-solid fa-floppy-disk"></i>');
            this.getMessageItem(messageId).addClass("is-editing-target");

            window.setTimeout(() => {
                $textarea.trigger("focus");
                const element = $textarea.get(0);
                if (element && element.setSelectionRange) {
                    const len = element.value.length;
                    element.setSelectionRange(len, len);
                }
                this.scheduleHeightAdjust(true);
            }, 40);
        },

        cancelComposerEdit: function (clearText) {
            const messageId = parseInt(this.state.editingMessageId, 10) || 0;
            this.state.editingMessageId = 0;
            this.state.editingOriginalText = "";

            const $form = $(this.config.formSelector).first();
            const $textarea = $(this.config.textareaSelector).first();
            const $sendButton = $form.find(".chat-send-btn").first();

            $form.removeClass("chat-form-editing").removeAttr("data-editing-message-id");
            $textarea.attr("placeholder", "Mesaj yaz...");
            if (clearText !== false) $textarea.val("");
            $sendButton.attr("aria-label", "Mesaj gönder").html('<i class="fa fa-paper-plane"></i>');
            this.getMessagesArea().find(".chat-message-item.is-editing-target").removeClass("is-editing-target");
            this.autoResizeTextarea($textarea.get(0));
            this.updateSelectedFileInfo();
            this.scheduleHeightAdjust(false);

            if (messageId > 0) {
                const $item = this.getMessageItem(messageId);
                $item.addClass("is-edited-pulse");
                window.setTimeout(() => $item.removeClass("is-edited-pulse"), 420);
            }
        },

        saveComposerEdit: function () {
            const self = this;
            const messageId = parseInt(self.state.editingMessageId, 10) || 0;
            if (messageId <= 0 || self.state.isEditingMessage) return;

            const $textarea = $(self.config.textareaSelector).first();
            const newText = ($textarea.val() || "").trim();
            if (!newText) {
                self.showError("Mesaj içeriği boş olamaz.");
                return;
            }

            self.state.isEditingMessage = true;
            const $form = $(self.config.formSelector).first();
            self.setFormSending($form, true);

            $.ajax({
                url: self.config.editMessageAction,
                type: "POST",
                data: { id: messageId, message: newText },
                success: function (response) {
                    const payload = self.normalizeEditMessageResponse(response);
                    if (!payload.success) {
                        self.showError(payload.message || "Mesaj güncellenemedi.");
                        return;
                    }
                    self.updateRenderedMessageText(messageId, payload.messageText || newText);
                    self.cancelComposerEdit(true);
                },
                error: function (xhr) {
                    if (!xhr.responseJSON?.moderationWarning) self.showError(self.getErrorMessage(xhr, "Mesaj güncellenemedi."));
                },
                complete: function () {
                    self.state.isEditingMessage = false;
                    self.setFormSending($form, false);
                }
            });
        },

        getMessagePlainText: function (messageId) {
            const $item = this.getMessageItem(messageId);
            if ($item.length === 0) return "";
            const html = $item.find(".chat-text").first().html() || "";
            return html
                .replace(/<br\s*\/?>/gi, "\n")
                .replace(/<[^>]*>/g, "")
                .replace(/&nbsp;/g, " ")
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'");
        },

        updateRenderedMessageText: function (messageId, messageText) {
            const $item = this.getMessageItem(messageId);
            if ($item.length === 0) return;
            const html = this.formatMessageText(messageText || "");
            let $text = $item.find(".chat-text").first();
            if ($text.length === 0) {
                $item.find(".chat-message-content").append(`<div class="chat-text-wrap"><p class="chat-text fs-08">${html}</p></div>`);
            } else {
                $text.html(html);
            }

            $item.addClass("is-edited-pulse is-edited").attr("data-edited", "true");
            $item.find(".chat-message-meta .chat-edited-label").remove();

            const $content = $item.find(".chat-message-content").first();
            if ($content.find(".chat-edited-inline-label").length === 0) {
                $content.append('<div class="chat-edited-inline-wrap"><span class="chat-edited-label chat-edited-inline-label fs-06" aria-label="Düzenlenmiş mesaj"><i class="fa fa-pen"></i> düzenlendi</span></div>');
            }

            window.setTimeout(() => $item.removeClass("is-edited-pulse"), 900);
            this.applyFontSizeUtilityClasses();
            this.cacheMessagesHtml();
        },

        reportMessage: function (messageId) {
            window.openChatReport(parseInt(messageId, 10) || 0);
        },

        withdrawMessageReport: function (messageId) {
            const self = this;
            messageId = parseInt(messageId, 10) || 0;
            if (messageId <= 0 || self.state.isWithdrawingReport) return;

            const $button = self.getMessageActionPopover(messageId).find("[data-chat-withdraw-report]");
            if ($button.prop("disabled")) return;

            const confirmed = window.confirm ? window.confirm("Bu mesaja ait şikayet geri çekilsin mi?") : true;
            if (!confirmed) return;

            self.state.isWithdrawingReport = true;
            $button.prop("disabled", true).addClass("is-withdrawing").find("span").text("Geri çekiliyor...");

            $.ajax({
                url: self.config.withdrawReportAction,
                type: "POST",
                data: { id: messageId },
                success: function (response) {
                    const payload = self.normalizeReportMessageResponse(response);
                    if (!payload.success) {
                        self.showError(payload.message || "Şikayet geri çekilemedi.");
                        return;
                    }
                    self.syncMessageReportedState(messageId, !!payload.reported);
                    self.loadMessageInfo(messageId);
                },
                error: function (xhr) {
                    self.showError(self.getErrorMessage(xhr, "Şikayet geri çekilemedi."));
                    self.loadMessageInfo(messageId);
                },
                complete: function () {
                    self.state.isWithdrawingReport = false;
                }
            });
        },

        deleteMessageForEveryone: function (messageId) {
            const self = this;
            messageId = parseInt(messageId, 10) || 0;
            if (messageId <= 0 || self.state.isDeletingMessage) return;

            const $button = self.getMessageActionPopover(messageId).find("[data-chat-delete-message]");
            if ($button.prop("disabled") && !self.isCurrentUserChatAdmin()) return;

            const confirmed = window.confirm ? window.confirm("Bu mesaj herkesten silinsin mi?") : true;
            if (!confirmed) return;

            self.state.isDeletingMessage = true;
            $button.prop("disabled", true).addClass("is-deleting").find("span").text("Siliniyor...");

            $.ajax({
                url: self.config.deleteMessageAction,
                type: "POST",
                data: {
                    id: messageId,
                    isAdmin: self.isCurrentUserChatAdmin()
                },
                success: function () {
                    self.removeDeletedMessageItems([messageId]);
                },
                error: function (xhr) {
                    self.showError(self.getErrorMessage(xhr, "Mesaj silinemedi."));
                    self.loadMessageInfo(messageId);
                },
                complete: function () {
                    self.state.isDeletingMessage = false;
                }
            });
        },

        normalizeMessageInfoResponse: function (response) {
            if (!response || typeof response !== "object") return { success: false, message: "Mesaj bilgisi alınamadı." };

            const hasSuccess = Object.prototype.hasOwnProperty.call(response, "success") || Object.prototype.hasOwnProperty.call(response, "Success");
            const success = hasSuccess ? !!(response.success !== undefined ? response.success : response.Success) : true;

            return {
                success: success,
                message: response.message || response.Message || "",
                usersMessageId: response.usersMessageId || response.UsersMessageId || 0,
                chatId: response.chatId || response.ChatId || 0,
                memberId: response.memberId || response.MemberId || 0,
                memberName: response.memberName || response.MemberName || "",
                memberNickName: response.memberNickName || response.MemberNickName || "",
                memberImg: response.memberImg || response.MemberImg || "",
                isMine: this.normalizeBoolean(response.isMine !== undefined ? response.isMine : response.IsMine),
                messageText: response.messageText || response.MessageText || "",
                isEdited: this.normalizeBoolean(response.isEdited !== undefined ? response.isEdited : response.IsEdited),
                editedDate: response.editedDate || response.EditedDate || null,
                hasImage: this.normalizeBoolean(response.hasImage !== undefined ? response.hasImage : response.HasImage),
                hasVideo: this.normalizeBoolean(response.hasVideo !== undefined ? response.hasVideo : response.HasVideo),
                hasFile: this.normalizeBoolean(response.hasFile !== undefined ? response.hasFile : response.HasFile),
                readedMembers: response.readedMembers || response.ReadedMembers || response.readMembers || response.ReadMembers || [],
                unreadedMembers: response.unreadedMembers || response.UnreadedMembers || response.unreadMembers || response.UnreadMembers || [],
                canViewReadReceipts: this.normalizeBoolean(response.canViewReadReceipts !== undefined ? response.canViewReadReceipts : response.CanViewReadReceipts),
                isExpiredReservationAnnouncement: this.normalizeBoolean(response.isExpiredReservationAnnouncement !== undefined ? response.isExpiredReservationAnnouncement : response.IsExpiredReservationAnnouncement),
                currentUserIsChatAdmin: this.normalizeBoolean(response.currentUserIsChatAdmin !== undefined ? response.currentUserIsChatAdmin : response.CurrentUserIsChatAdmin),
                canDeleteForEveryone: this.normalizeBoolean(response.canDeleteForEveryone !== undefined ? response.canDeleteForEveryone : response.CanDeleteForEveryone),
                deleteDisabledReason: response.deleteDisabledReason || response.DeleteDisabledReason || "",
                canEditText: this.normalizeBoolean(response.canEditText !== undefined ? response.canEditText : response.CanEditText),
                editDisabledReason: response.editDisabledReason || response.EditDisabledReason || "",
                reported: this.normalizeBoolean(response.reported !== undefined ? response.reported : response.Reported),
                canReport: this.normalizeBoolean(response.canReport !== undefined ? response.canReport : response.CanReport),
                reportDisabledReason: response.reportDisabledReason || response.ReportDisabledReason || "",
                canWithdrawReport: this.normalizeBoolean(response.canWithdrawReport !== undefined ? response.canWithdrawReport : response.CanWithdrawReport),
                withdrawReportDisabledReason: response.withdrawReportDisabledReason || response.WithdrawReportDisabledReason || "",
                deleteExpiresAt: response.deleteExpiresAt || response.DeleteExpiresAt || null,
                editExpiresAt: response.editExpiresAt || response.EditExpiresAt || null,
                serverNow: response.serverNow || response.ServerNow || null
            };
        },

        normalizeEditMessageResponse: function (response) {
            if (!response || typeof response !== "object") return { success: true, messageText: "" };
            const hasSuccess = Object.prototype.hasOwnProperty.call(response, "success") || Object.prototype.hasOwnProperty.call(response, "Success");
            return {
                success: hasSuccess ? !!(response.success !== undefined ? response.success : response.Success) : true,
                message: response.message || response.Message || "",
                messageText: response.messageText || response.MessageText || "",
                isEdited: response.isEdited !== undefined ? response.isEdited : response.IsEdited,
                editedDate: response.editedDate || response.EditedDate || null
            };
        },

        normalizeReportMessageResponse: function (response) {
            if (!response || typeof response !== "object") return { success: true, reported: true };
            const hasSuccess = Object.prototype.hasOwnProperty.call(response, "success") || Object.prototype.hasOwnProperty.call(response, "Success");
            return {
                success: hasSuccess ? !!(response.success !== undefined ? response.success : response.Success) : true,
                message: response.message || response.Message || "",
                reported: response.reported !== undefined ? response.reported : response.Reported
            };
        },

        getMessageItem: function (messageId) {
            return this.getMessagesArea()
                .children(`.chat-message-item[data-id="${this.escapeJquerySelectorValue(messageId)}"]`)
                .first();
        },

        syncMessageReportedState: function (messageId, isReported) {
            const $item = this.getMessageItem(messageId);
            if ($item.length === 0) return;
            $item.toggleClass("is-reported", !!isReported).attr("data-reported", isReported ? "true" : "false");
            const $content = $item.find(".chat-message-content").first();
            if (isReported && $content.find(".chat-reported-badge").length === 0) {
                $content.prepend('<div class="chat-reported-badge fs-06" aria-label="Bu mesaj şikayet edildi"><i class="fa fa-flag"></i><span class="' + ($item.hasClass("is-mine") ? "text-white" : "text-red") + '">Şikayet Edildi</span></div>');
            }
            if (!isReported) $content.find(".chat-reported-badge").remove();
            this.cacheMessagesHtml();
        },

        renderActionCard: function (actionId, actionTypeId, memberName, memberImage, timeText, isMine) {
            return `
                <div class="chat-structured-message-wrap">
                    <div class="globalRequestCard chat-action-card w-100" data-id="${this.safeAttr(actionId)}" data-type="${this.safeAttr(actionTypeId)}">
                        <div class="card m-0 shadow-none border-ff br-12px opacity-50">
                            <div class="card-body">
                                <div class="row m-0 p-0">
                                    <div class="col-auto d-flex align-items-center p-0">
                                        <div class="slider-container border-ff chat-image-box">
                                            <div class="slider-track">
                                                <img src="https://sahasenin.com/imageNull.webp" class="slider-item" alt="işlem">
                                            </div>
                                            <div class="dots"></div>
                                        </div>
                                    </div>
                                    <div class="col d-flex flex-column justify-content-between pr-0 ps-2"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    ${this.renderStructuredMessageSenderInfo(memberName, memberImage, timeText, isMine)}
                </div>
            `;
        },

        renderObjectCard: function (objectId, objectTypeId, memberName, memberImage, timeText, isMine) {
            return `
                <div class="chat-structured-message-wrap">
                    <div class="globalObjectCard chat-object-card w-100" data-id="${this.safeAttr(objectId)}" data-type="${this.safeAttr(objectTypeId)}">
                        <div class="card m-0 shadow-none border-ff br-12px opacity-50">
                            <div class="card-body">
                                <div class="row m-0 p-0">
                                    <div class="col-auto d-flex align-items-center p-0">
                                        <div class="slider-container border-ff chat-image-box">
                                            <div class="slider-track">
                                                <img src="https://sahasenin.com/imageNull.webp" class="slider-item" alt="işlem">
                                            </div>
                                            <div class="dots"></div>
                                        </div>
                                    </div>
                                    <div class="col d-flex flex-column justify-content-between pr-0 ps-2"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    ${this.renderStructuredMessageSenderInfo(memberName, memberImage, timeText, isMine)}
                </div>
            `;
        },

        renderStructuredMessageSenderInfo: function (memberName, memberImage, timeText, isMine) {
            return `
                <div class="chat-card-sender-info ${isMine ? "is-meta-mine" : "is-meta-other"}">
                    <span class="chat-meta-member">
                        <img src="${memberImage}" class="chat-avatar" alt="${memberName}">
                        <span class="chat-member-name fs-06 ${isMine ? "text-dark" : ""}">${memberName}</span>
                    </span>
                    <span class="chat-time fs-06 ${isMine ? "text-dark" : ""}">${this.safeText(timeText)}</span>
                </div>
            `;
        },

        renderDateBadge: function (dateKey) {
            return `
                <div class="chat-date-badge" data-date="${this.safeAttr(dateKey)}">
                    <span class="fs-06">${this.safeText(this.formatDisplayDate(dateKey))}</span>
                </div>
            `;
        },

        renderEmptyState: function () {
            return `
                <div class="chat-empty-state">
                    <div class="chat-empty-icon"><i class="fa fa-comments"></i></div>
                    <strong class="fs-09">Henüz Mesaj Yok</strong>
                    <span class="fs-07">İlk mesajı yazarak sohbeti başlatabilirsiniz.</span>
                </div>
            `;
        },

        afterMessagesRendered: function (scrollToBottom) {
            this.normalizeMessageGroups();
            this.ensureFloatingDateBadge();
            this.updateActiveDateBadge();
            if (scrollToBottom) this.startInitialBottomPinning();
            this.updateGlobalRequestCards();
            this.applyFontSizeUtilityClasses();
            if (scrollToBottom) this.scrollToBottom(false);
            this.adjustHeight(!!scrollToBottom);
            if (scrollToBottom) this.scrollToBottom(false);
            this.cacheMessagesHtml();
        },

        afterOlderMessagesRendered: function () {
            this.normalizeMessageGroups();
            this.ensureFloatingDateBadge();
            this.updateActiveDateBadge();
            this.updateGlobalRequestCards();
            this.applyFontSizeUtilityClasses();
            this.cacheMessagesHtml();
        },

        cacheMessagesHtml: function () {
            try {
                const $messagesArea = this.getMessagesArea().clone();
                $messagesArea.find(".chat-message-action-popover").remove();
                sessionStorage.setItem(`chat_cache_${this.state.chatId}`, $messagesArea.html());
            } catch (e) { window.console && window.console.warn("Cache yazma hatası", e); }
        },

        prepareForPrepend: function (messages) {
            const firstExistingDateKey = this.getFirstMessageDateKey();
            if (!firstExistingDateKey || !messages || messages.length === 0) return;

            if (this.messageListContainsDate(messages, firstExistingDateKey)) {
                this.getMessagesArea()
                    .children(".chat-date-badge")
                    .filter(function () { return String($(this).data("date")) === String(firstExistingDateKey); })
                    .first().remove();
            }
        },

        filterNewMessages: function (messages) {
            const self = this;
            return (messages || []).filter(msg => {
                const messageId = self.getMessageId(msg);
                return !messageId || !self.messageExists(messageId);
            });
        },

        sortMessages: function (messages) {
            return (messages || []).slice().sort((a, b) => {
                const ad = (SahaseninTime.parse(a.SendDate || a.sendDate || 0)?.getTime() || 0) || 0;
                const bd = (SahaseninTime.parse(b.SendDate || b.sendDate || 0)?.getTime() || 0) || 0;
                if (ad !== bd) return ad - bd;

                const ai = parseInt(a.UsersMessageId || a.usersMessageId || a.Id || a.id || 0, 10) || 0;
                const bi = parseInt(b.UsersMessageId || b.usersMessageId || b.Id || b.id || 0, 10) || 0;
                return ai - bi;
            });
        },

        messageExists: function (messageId) {
            if (!messageId) return false;
            return this.getMessagesArea()
                .find(".chat-message-item")
                .filter(function () { return String($(this).data("id")) === String(messageId); })
                .length > 0;
        },

        messageListContainsDate: function (messages, dateKey) {
            return messages.some(msg => this.getMessageDateKey(msg) === dateKey);
        },

        removeEmptyState: function () {
            this.getMessagesArea().find(".chat-empty-state").remove();
        },

        scrollToBottom: function (animated) {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return;

            const area = $messagesArea.get(0);
            if (!area) return;

            const executeScroll = () => {
                const scrollHeight = area.scrollHeight || $messagesArea.prop("scrollHeight") || 0;
                if (animated) {
                    $messagesArea.stop(true).animate({ scrollTop: scrollHeight }, 160);
                } else {
                    area.scrollTop = scrollHeight;
                }
                this.state.isNearBottom = true;
            };

            executeScroll();
            window.requestAnimationFrame(executeScroll);
            window.setTimeout(executeScroll, 40);
            window.setTimeout(executeScroll, 140);

            $messagesArea.find("img").each(function () {
                if (this.complete) return;
                $(this)
                    .off("load.chatBottomFix error.chatBottomFix")
                    .one("load.chatBottomFix error.chatBottomFix", executeScroll);
            });
        },

        startInitialBottomPinning: function () {
            if (this.state.initialBottomPinCompleted || this.state.initialBottomPinActive) return;

            const selector = `.globalRequestCard[data-type="${this.config.reservationAnnouncementActionTypeId}"]:not(.init)`;
            const $cards = this.getMessagesArea().find(selector);
            if ($cards.length === 0) {
                this.state.initialBottomPinCompleted = true;
                return;
            }

            const area = this.getMessagesArea().get(0);
            if (!area) return;

            this.state.initialBottomPinActive = true;
            $cards.attr("data-chat-initial-bottom-pending", "true");

            if (typeof window.ResizeObserver === "function") {
                this.state.initialBottomPinObserver = new window.ResizeObserver(() => {
                    this.scheduleInitialBottomPin();
                });
                $cards.each((_index, card) => this.state.initialBottomPinObserver.observe(card));
            }

            this.scheduleInitialBottomPin();
        },

        scheduleInitialBottomPin: function () {
            if (!this.state.initialBottomPinActive || this.state.initialBottomPinFrame) return;

            this.state.initialBottomPinFrame = window.requestAnimationFrame(() => {
                this.state.initialBottomPinFrame = null;
                if (!this.state.initialBottomPinActive) return;

                const area = this.getMessagesArea().get(0);
                if (!area) return;
                area.scrollTop = area.scrollHeight;
                this.state.isNearBottom = true;
            });
        },

        onGlobalRequestCardLoaded: function (element, result) {
            if (!element) return;

            const actionTypeId = parseInt((result && result.type) || $(element).data("type") || 0, 10) || 0;
            if (actionTypeId === this.config.reservationAnnouncementActionTypeId) {
                const isExpired = $(element).find(".reservation-announcement-card.is-expired").length > 0;
                $(element)
                    .closest(".chat-message-item")
                    .toggleClass("is-expired-reservation-announcement", isExpired)
                    .attr("data-reservation-announcement-expired", isExpired ? "true" : "false");
            }

            if (!this.state.initialBottomPinActive) return;
            if (actionTypeId !== this.config.reservationAnnouncementActionTypeId ||
                $(element).attr("data-chat-initial-bottom-pending") !== "true") return;

            $(element).removeAttr("data-chat-initial-bottom-pending");
            this.scheduleInitialBottomPin();

            if (this.getMessagesArea().find('[data-chat-initial-bottom-pending="true"]').length > 0) return;

            if (this.state.initialBottomPinSettleTimer) window.clearTimeout(this.state.initialBottomPinSettleTimer);
            this.state.initialBottomPinSettleTimer = window.setTimeout(() => {
                this.scheduleInitialBottomPin();
                window.requestAnimationFrame(() => this.finishInitialBottomPinning());
            }, 200);
        },

        finishInitialBottomPinning: function () {
            if (this.state.initialBottomPinObserver) {
                this.state.initialBottomPinObserver.disconnect();
                this.state.initialBottomPinObserver = null;
            }
            if (this.state.initialBottomPinFrame) {
                window.cancelAnimationFrame(this.state.initialBottomPinFrame);
                this.state.initialBottomPinFrame = null;
            }
            if (this.state.initialBottomPinSettleTimer) {
                window.clearTimeout(this.state.initialBottomPinSettleTimer);
                this.state.initialBottomPinSettleTimer = null;
            }

            this.getMessagesArea().find('[data-chat-initial-bottom-pending="true"]').removeAttr("data-chat-initial-bottom-pending");
            this.state.initialBottomPinActive = false;
            this.state.initialBottomPinCompleted = true;
        },

        isNearBottom: function () {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return true;

            const scrollTop = $messagesArea.scrollTop() || 0;
            const scrollHeight = $messagesArea.prop("scrollHeight") || 0;
            const clientHeight = $messagesArea.outerHeight() || 0;

            return (scrollHeight - (scrollTop + clientHeight)) <= this.config.bottomThresholdPx;
        },

        ensureScrollStabilityStyles: function () {
            if (document.getElementById("chat-scroll-stability-styles")) return;

            const style = document.createElement("style");
            style.id = "chat-scroll-stability-styles";
            style.textContent = `
                ${this.config.messagesAreaSelector}, ${this.config.messagesAreaFallbackSelector} {
                    position: relative;
                    overflow-anchor: none !important;
                    scroll-behavior: auto;
                }

                ${this.config.messagesAreaSelector} .chat-top-loader,
                ${this.config.messagesAreaFallbackSelector} .chat-top-loader {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 20;
                    pointer-events: none;
                }
            `;
            document.head.appendChild(style);
        },

        captureScrollAnchor: function () {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return null;

            const area = $messagesArea.get(0);
            if (!area) return null;

            const areaRect = area.getBoundingClientRect();
            const $items = $messagesArea.children(".chat-message-item[data-id]").not(".chat-top-loader, .chat-loader, .chat-empty-state");
            if ($items.length === 0) return null;

            let chosen = null;
            $items.each(function () {
                const rect = this.getBoundingClientRect();
                if (rect.bottom <= areaRect.top + 1) return true;
                chosen = this;
                return false;
            });

            if (!chosen) chosen = $items.get(0);
            if (!chosen) return null;

            const rect = chosen.getBoundingClientRect();
            return {
                messageId: String($(chosen).data("id") || ""),
                offsetTop: rect.top - areaRect.top,
                scrollTopBefore: area.scrollTop || 0,
                scrollHeightBefore: area.scrollHeight || 0
            };
        },

        restoreScrollAnchor: function (anchor) {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0 || !anchor) return false;

            const area = $messagesArea.get(0);
            if (!area) return false;

            let $target = anchor.messageId
                ? $messagesArea.children(`.chat-message-item[data-id="${this.escapeJquerySelectorValue(anchor.messageId)}"]`).first()
                : $();

            if ($target.length === 0) {
                $target = $messagesArea.children(".chat-message-item[data-id]").first();
            }

            if ($target.length === 0) {
                if (anchor.scrollHeightBefore) {
                    area.scrollTop = (anchor.scrollTopBefore || 0) + Math.max(0, (area.scrollHeight || 0) - anchor.scrollHeightBefore);
                    return true;
                }
                return false;
            }

            const areaRect = area.getBoundingClientRect();
            const targetRect = $target.get(0).getBoundingClientRect();
            const expectedTop = areaRect.top + (anchor.offsetTop || 0);
            const delta = targetRect.top - expectedTop;

            if (Math.abs(delta) > 0.5) {
                area.scrollTop = (area.scrollTop || 0) + delta;
            }

            return true;
        },

        scheduleOlderScrollRestore: function (anchor) {
            const self = this;
            if (!anchor) {
                self.state.isRestoringOlderScroll = false;
                self.state.olderScrollAnchor = null;
                return;
            }

            self.state.olderScrollAnchor = anchor;
            self.state.isRestoringOlderScroll = true;

            if (self.state.olderScrollRestoreFrame) {
                window.cancelAnimationFrame(self.state.olderScrollRestoreFrame);
                self.state.olderScrollRestoreFrame = null;
            }

            let pass = 0;
            const tick = () => {
                if (!self.state.olderScrollAnchor) return;
                self.restoreScrollAnchor(self.state.olderScrollAnchor);
                pass += 1;

                if (pass < 5) {
                    self.state.olderScrollRestoreFrame = window.requestAnimationFrame(tick);
                    return;
                }

                window.setTimeout(() => {
                    if (self.state.olderScrollAnchor) {
                        self.restoreScrollAnchor(self.state.olderScrollAnchor);
                    }
                    self.state.isRestoringOlderScroll = false;
                    self.state.olderScrollAnchor = null;
                    if (self.state.olderScrollRestoreFrame) {
                        window.cancelAnimationFrame(self.state.olderScrollRestoreFrame);
                        self.state.olderScrollRestoreFrame = null;
                    }
                }, 120);
            };

            self.state.olderScrollRestoreFrame = window.requestAnimationFrame(tick);
        },

        watchPrependedMedia: function ($batch) {
            const self = this;
            if (!$batch || $batch.length === 0) return;

            const reanchor = function () {
                if (!self.state.olderScrollAnchor) return;
                self.restoreScrollAnchor(self.state.olderScrollAnchor);
            };

            $batch.find("img").each(function () {
                if (this.complete) return;
                $(this).one("load.chatOlderFix error.chatOlderFix", reanchor);
            });
        },

        showLoader: function (text) {
            const $messagesArea = this.getMessagesArea();
            $messagesArea.addClass("chat-loading");
            const loaderHtml = `<div class="chat-loader text-center text-muted py-2 fs-07">${this.safeText(text || "Yükleniyor...")}</div>`;

            if ($messagesArea.find(".chat-loader").length === 0) {
                $messagesArea.prepend(loaderHtml);
            } else {
                $messagesArea.find(".chat-loader").text(text || "Yükleniyor...");
            }
        },

        hideLoader: function () {
            const $messagesArea = this.getMessagesArea();
            $messagesArea.removeClass("chat-loading");
            $messagesArea.find(".chat-loader").remove();
        },

        showTopLoader: function () {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.find(".chat-top-loader").length === 0) {
                $messagesArea.prepend('<div class="chat-top-loader text-center text-muted py-2 fs-07" aria-hidden="true">Eski mesajlar yükleniyor...</div>');
            }
        },

        hideTopLoader: function () {
            this.getMessagesArea().find(".chat-top-loader").remove();
        },

        setFormSending: function ($form, isSending) {
            $form = $form && $form.length ? $form : $(this.config.formSelector).first();
            if (!$form.length) return;

            const $submit = $form.find('button[type="submit"], input[type="submit"]').first();
            const $sendButton = $form.find(".chat-send-btn").first();

            $form.toggleClass("chat-form-sending", !!isSending);
            $submit.prop("disabled", !!isSending);
            $form.find(".chat-attachment-btn, .chat-attachment-menu-btn, [data-chat-emoji-toggle], [data-chat-attachment-menu-toggle]").prop("disabled", !!isSending);

            if ($sendButton.length === 0) return;

            if (isSending) {
                if ($sendButton.data("chat-original-html") === undefined) {
                    $sendButton.data("chat-original-html", $sendButton.html());
                }
                $sendButton
                    .addClass("is-loading")
                    .attr("aria-label", "Gönderiliyor")
                    .html('<span class="chat-send-loader" aria-hidden="true"></span>');
            } else {
                const originalHtml = $sendButton.data("chat-original-html");
                const isEditing = $form.hasClass("chat-form-editing");
                const fallbackHtml = isEditing ? '<i class="fa-solid fa-floppy-disk"></i>' : '<i class="fa fa-paper-plane"></i>';
                $sendButton
                    .removeClass("is-loading")
                    .removeAttr("data-chat-upload-progress")
                    .removeData("chat-original-html")
                    .attr("aria-label", isEditing ? "Düzenlemeyi kaydet" : "Mesaj gönder")
                    .html((originalHtml !== undefined && isEditing) ? originalHtml : fallbackHtml);
            }
        },

        updateSendButtonProgress: function ($form, percent) {
            $form = $form && $form.length ? $form : $(this.config.formSelector).first();
            const $sendButton = $form.find(".chat-send-btn").first();
            if ($sendButton.length === 0 || !$sendButton.hasClass("is-loading")) return;
            percent = Math.max(1, Math.min(99, parseInt(percent, 10) || 0));
            $sendButton.attr("data-chat-upload-progress", percent);
        },

        clearFormInputs: function () {
            const $textarea = $(this.config.textareaSelector);
            $textarea.val("");
            $(this.config.imageInputSelector).val("");
            $(this.config.videoInputSelector).val("");
            $(this.config.gifInputSelector).val("");
            $(this.config.stickerInputSelector).val("");
            $(this.config.fileInputSelector).val("");
            this.updateSelectedFileInfo();
            this.closeAttachmentMenu();
            this.closeEmojiPanel();
            this.autoResizeTextarea($textarea.get(0));
            this.scheduleHeightAdjust(true);
        },

        clearSelectedFiles: function () {
            $(this.config.imageInputSelector).val("");
            $(this.config.videoInputSelector).val("");
            $(this.config.gifInputSelector).val("");
            $(this.config.stickerInputSelector).val("");
            $(this.config.fileInputSelector).val("");
            this.updateSelectedFileInfo();
            this.scheduleHeightAdjust(false);
        },

        hasSelectedFile: function (selector) {
            const element = $(selector).get(0);
            return !!(element && element.files && element.files.length > 0);
        },

        updateGlobalRequestCards: function () {
            const $requestCards = $(".globalRequestCard:not(.chat-structured-init-requested)");
            const $objectCards = $(".globalObjectCard:not(.init)");

            $requestCards.add($objectCards).addClass("w-100");
            $requestCards.addClass("chat-structured-init-requested");

            if ($requestCards.length > 0 && typeof window.updateGlobalRequestCards === "function") {
                window.updateGlobalRequestCards();
            }

            if ($objectCards.length > 0 && typeof window.updateGlobalObjectCards === "function") {
                window.updateGlobalObjectCards();
            }
        },

        ensureChatForm: function () {
            if (String($("#ChatCommunicationBlocked").val() || "false").toLowerCase() === "true") {
                $(this.config.chatInputSelector).remove();
                return $();
            }

            const $form = $(this.config.formSelector);
            if ($form.length > 0) {
                this.ensureEnhancedChatForm($form.first());
                return $form.first();
            }

            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return $();

            let $chatInput = $(this.config.chatInputSelector).first();
            if ($chatInput.length === 0) {
                $chatInput = $('<div class="chat-input-container"></div>');
                $messagesArea.after($chatInput);
            }

            $chatInput.html(this.createChatFormHtml());
            this.applyFontSizeUtilityClasses();
            return $(this.config.formSelector).first();
        },

        ensureEnhancedChatForm: function ($form) {
            if (!$form || $form.length === 0) return;

            const inputHtml = [
                [this.config.imageInputSelector, '<input type="file" id="imageInput" name="Image" accept="image/*" hidden>'],
                [this.config.videoInputSelector, '<input type="file" id="videoInput" name="Video" accept="video/*" hidden>'],
                [this.config.gifInputSelector, '<input type="file" id="gifInput" name="Gif" accept="image/gif" hidden>'],
                [this.config.stickerInputSelector, '<input type="file" id="stickerInput" name="Sticker" accept="image/png,image/webp,image/gif,image/jpeg" hidden>'],
                [this.config.fileInputSelector, '<input type="file" id="fileInput" name="File" hidden>']
            ];

            inputHtml.forEach(([selector, html]) => {
                if ($(selector).length === 0) $form.prepend(html);
            });

            const $textarea = $form.find(this.config.textareaSelector).first().addClass("fs-09");
            const $submit = $form.find("button[type='submit'], input[type='submit']").first();
            const $insertBefore = $textarea.length ? $textarea : $submit;

            // Eski sürümden kalan doğrudan görünen ek butonlarını temizle.
            $form.children('[data-chat-file-trigger]').remove();
            $form.children('.chat-upload-status').remove();
            $form.next('.chat-upload-status').remove();

            if ($form.find('[data-chat-emoji-toggle]').length === 0 && $insertBefore.length) {
                $('<a class="chat-attachment-btn" data-chat-emoji-toggle aria-label="Emoji ekle"><i class="fa-regular fa-face-smile"></i></a>').insertBefore($insertBefore);
            }

            if ($form.find('[data-chat-attachment-menu-toggle]').length === 0 && $insertBefore.length) {
                $('<a class="chat-attachment-btn chat-attachment-plus-btn" data-chat-attachment-menu-toggle aria-label="Ek ekle" aria-expanded="false"><i class="fa fa-plus"></i></a>').insertBefore($insertBefore);
            }

            if ($form.find('.chat-attachment-menu').length === 0 && $insertBefore.length) {
                $(this.renderAttachmentMenu()).insertBefore($insertBefore);
            }

            if ($form.next(".chat-emoji-panel").length === 0) $(this.renderEmojiPanel()).insertAfter($form);
            if ($(".chat-selected-files").length === 0) $('<div class="chat-selected-files fs-07" aria-live="polite"></div>').insertAfter($form.next(".chat-emoji-panel"));

            this.applyFontSizeUtilityClasses();
            this.autoResizeTextarea($textarea.get(0));
        },

        createChatFormHtml: function () {
            const chatId = this.state.chatId || this.resolveChatId(this.getMessagesArea()) || "";
            const currentUserId = $(this.config.currentUserIdSelector).val() || this.getMessagesArea().data("current-user") || this.getMessagesArea().data("current-user-id") || "";
            const includeChatIdHidden = $(this.config.chatIdSelector).length === 0;
            const includeCurrentUserHidden = $(this.config.currentUserIdSelector).length === 0;

            let html = `<form id="chatForm" class="chat-form" action="${this.safeAttr(this.config.sendAction)}" method="post" enctype="multipart/form-data" autocomplete="off">`;

            if (includeChatIdHidden) {
                html += `<input type="hidden" id="ChatId" name="ChatId" value="${this.safeAttr(chatId)}">`;
            }
            if (includeCurrentUserHidden) {
                html += `<input type="hidden" id="CurrentUserId" name="CurrentUserId" value="${this.safeAttr(currentUserId)}">`;
            }

            html += `
                <input type="file" id="imageInput" name="Image" accept="image/*" hidden>
                <input type="file" id="videoInput" name="Video" accept="video/*" hidden>
                <input type="file" id="gifInput" name="Gif" accept="image/gif" hidden>
                <input type="file" id="stickerInput" name="Sticker" accept="image/png,image/webp,image/gif,image/jpeg" hidden>
                <input type="file" id="fileInput" name="File" hidden>
                <a class="chat-edit-cancel-mode-btn fs-07" data-chat-cancel-edit-mode aria-label="iptal"><i class="fa fa-times"></i><span class="text-red ms-1">iptal</span></a>
                <a class="chat-attachment-btn" data-chat-emoji-toggle aria-label="Emoji ekle"><i class="fa-regular fa-face-smile"></i></a>
                <a class="chat-attachment-btn chat-attachment-plus-btn" data-chat-attachment-menu-toggle aria-label="Ek ekle" aria-expanded="false"><i class="fa fa-plus"></i></a>
                ${this.renderAttachmentMenu()}
                <textarea id="messageText" class="fs-09" name="Message" rows="1" maxlength="2000" placeholder="Mesaj yaz..."></textarea>
                <button type="submit" class="chat-send-btn" aria-label="Mesaj gönder"><i class="fa fa-paper-plane"></i></button>
            </form>
            ${this.renderEmojiPanel()}
            <div class="chat-selected-files fs-07" aria-live="polite"></div>
            `;

            return html;
        },

        getViewportHeight: function () {
            const visualViewport = window.visualViewport;
            if (visualViewport && visualViewport.height) {
                return Math.floor(visualViewport.height);
            }
            return Math.floor(window.innerHeight || $(window).height() || 0);
        },

        getSafeAreaInsets: function () {
            const rootStyle = window.getComputedStyle(document.documentElement);
            const parse = name => Math.max(0, parseFloat(rootStyle.getPropertyValue(name)) || 0);
            const mauiTop = parse('--maui-safe-area-top');
            const mauiBottom = parse('--maui-safe-area-bottom');
            const probe = document.createElement('div');
            probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)';
            document.body.appendChild(probe);
            const probeStyle = window.getComputedStyle(probe);
            const browserTop = parseFloat(probeStyle.paddingTop) || 0;
            const browserBottom = parseFloat(probeStyle.paddingBottom) || 0;
            probe.remove();
            return {
                top: Math.max(mauiTop, browserTop),
                bottom: Math.max(mauiBottom, browserBottom)
            };
        },

        getPartialBodyHeight: function () {
            const $partialBody = $(this.config.partialBodySelector).first();
            if ($partialBody.length > 0) return $partialBody.innerHeight() || $partialBody.height() || 0;
            return this.getViewportHeight();
        },

        getMobileFooterHeight: function () {
            if (this.state.isKeyboardOpen || document.body.classList.contains("chat-keyboard-open")) return 0;
            let height = 0;
            const self = this;
            $(this.config.mobileFooterSelector).each(function () {
                const $footer = $(this);
                if (!self.isElementVisible($footer)) return true;
                height = Math.max(height, $footer.outerHeight(true) || self.config.mobileFooterFallbackHeightPx);
                return false;
            });
            return height || 0;
        },

        getOpenProfileHeight: function () {
            let height = 0;
            const self = this;
            $(this.config.topUserSelector).each(function () {
                const $profile = $(this);
                if (!self.isElementVisible($profile)) return true;
                height = Math.max(height, $profile.outerHeight(true) || 0);
                return false;
            });
            return height || 0;
        },

        isElementVisible: function ($element) {
            return !!($element && $element.length && !$element.hasClass("d-none") && $element.is(":visible"));
        },

        autoResizeTextarea: function (textarea) {
            if (!textarea) return;
            const minHeight = 36;
            const maxHeight = 118;
            textarea.style.height = `${minHeight}px`;
            const nextHeight = Math.max(minHeight, Math.min(textarea.scrollHeight || minHeight, maxHeight));
            textarea.style.height = `${nextHeight}px`;
            textarea.style.overflowY = nextHeight >= maxHeight ? "auto" : "hidden";
        },

        triggerAttachmentInput: function (type) {
            this.closeEmojiPanel();
            const selectorMap = {
                image: this.config.imageInputSelector,
                video: this.config.videoInputSelector,
                gif: this.config.gifInputSelector,
                sticker: this.config.stickerInputSelector,
                file: this.config.fileInputSelector
            };
            const selector = selectorMap[type] || this.config.fileInputSelector;
            const element = $(selector).get(0);
            if (element) element.click();
        },

        updateSelectedFileInfo: function () {
            const $holder = $(".chat-selected-files").first();
            if ($holder.length === 0) return;

            const summary = this.getSelectedFileSummary();
            if (!summary) {
                $holder.empty().removeClass("has-files");
                return;
            }

            $holder
                .addClass("has-files")
                .html(`<span class="fs-07"><i class="fa fa-paperclip"></i> ${this.safeText(summary)}</span><button type="button" class="fs-07" data-chat-clear-files aria-label="Ekleri temizle"><i class="fa fa-times"></i></button>`);
        },

        getSelectedFileSummary: function () {
            const names = [];
            const inputMap = [
                { selector: this.config.imageInputSelector, label: "Resim" },
                { selector: this.config.videoInputSelector, label: "Video" },
                { selector: this.config.gifInputSelector, label: "GIF" },
                { selector: this.config.stickerInputSelector, label: "Çıkartma" },
                { selector: this.config.fileInputSelector, label: "Dosya" }
            ];

            inputMap.forEach(item => {
                const input = $(item.selector).get(0);
                if (input && input.files && input.files.length > 0) {
                    names.push(input.files[0].name || item.label);
                }
            });

            return names.join(", ");
        },

        hasAnySelectedAttachment: function () {
            return [
                this.config.imageInputSelector,
                this.config.videoInputSelector,
                this.config.gifInputSelector,
                this.config.stickerInputSelector,
                this.config.fileInputSelector
            ].some(selector => this.hasSelectedFile(selector));
        },

        setUploadStatus: function () {
            // Eski görsel durum alanı bilinçli olarak devre dışı.
            // Gönderim/yükleme durumu artık doğrudan .chat-send-btn içinde loader olarak gösteriliyor.
        },

        renderAttachmentMenu: function () {
            return `
                <div class="chat-attachment-menu" aria-label="Ek seçenekleri" aria-hidden="true">
                    <button type="button" class="chat-attachment-menu-btn fs-07" data-chat-file-trigger="image" aria-label="Resim ekle"><i class="fa fa-image"></i><span>Resim</span></button>
                    <button type="button" class="chat-attachment-menu-btn fs-07" data-chat-file-trigger="video" aria-label="Video ekle"><i class="fa fa-video"></i><span>Video</span></button>
                    <button type="button" class="chat-attachment-menu-btn fs-07" data-chat-file-trigger="gif" aria-label="GIF ekle"><strong class="fs-06">GIF</strong><span>GIF</span></button>
                    <button type="button" class="chat-attachment-menu-btn fs-07" data-chat-file-trigger="sticker" aria-label="Çıkartma ekle"><i class="fa-regular fa-note-sticky"></i><span>Çıkartma</span></button>
                    <button type="button" class="chat-attachment-menu-btn fs-07" data-chat-file-trigger="file" aria-label="Dosya ekle"><i class="fa fa-paperclip"></i><span>Dosya</span></button>
                </div>
            `;
        },

        renderEmojiPanel: function () {
            const emojis = ["😀", "😄", "😂", "🙂", "😉", "😍", "🥳", "😎", "🤝", "👏", "👍", "👎", "🙏", "💪", "⚽", "🥅", "🏆", "🔥", "⭐", "❤️", "💚", "✅", "❌", "📸", "🎥", "📎", "🎉", "🙌", "🤔", "😅"];
            return `
                <div class="chat-emoji-panel" aria-label="Emoji seç" aria-hidden="true">
                    ${emojis.map(emoji => `<button type="button" class="chat-emoji-option fs-09" data-chat-emoji="${this.safeAttr(emoji)}">${emoji}</button>`).join("")}
                </div>
            `;
        },

        toggleEmojiPanel: function () {
            this.closeAttachmentMenu();
            const $panel = $(".chat-emoji-panel").first();
            if ($panel.length === 0) return;
            this.state.isEmojiPanelOpen = !$panel.hasClass("is-visible");
            $panel.toggleClass("is-visible", this.state.isEmojiPanelOpen).attr("aria-hidden", this.state.isEmojiPanelOpen ? "false" : "true");
            this.scheduleHeightAdjust(false);
        },

        closeEmojiPanel: function () {
            const $panel = $(".chat-emoji-panel").first();

            this.state.isEmojiPanelOpen = false;

            if ($panel.length > 0) {
                $panel.removeClass("is-visible").attr("aria-hidden", "true");
            }

            window.requestAnimationFrame(() => {
                this.scheduleHeightAdjust(false);
            });
        },

        toggleAttachmentMenu: function () {
            const $form = $(this.config.formSelector).first();
            const $menu = $form.find(".chat-attachment-menu").first();
            if ($menu.length === 0) return;

            this.closeEmojiPanel();
            this.state.isAttachmentMenuOpen = !$menu.hasClass("is-visible");
            $menu.toggleClass("is-visible", this.state.isAttachmentMenuOpen).attr("aria-hidden", this.state.isAttachmentMenuOpen ? "false" : "true");
            $form.toggleClass("chat-attachment-menu-open", this.state.isAttachmentMenuOpen);
            $form.find("[data-chat-attachment-menu-toggle]").attr("aria-expanded", this.state.isAttachmentMenuOpen ? "true" : "false");
            this.scheduleHeightAdjust(false);
        },

        closeAttachmentMenu: function () {
            const $form = $(this.config.formSelector).first();
            const $menu = $form.find(".chat-attachment-menu").first();
            this.state.isAttachmentMenuOpen = false;
            if ($menu.length) $menu.removeClass("is-visible").attr("aria-hidden", "true");
            $form.removeClass("chat-attachment-menu-open");
            $form.find("[data-chat-attachment-menu-toggle]").attr("aria-expanded", "false");
        },

        insertEmojiAtCursor: function (emoji) {
            emoji = emoji === undefined || emoji === null ? "" : String(emoji);
            if (!emoji) return;

            const textarea = $(this.config.textareaSelector).get(0);
            if (!textarea) return;

            const start = textarea.selectionStart || 0;
            const end = textarea.selectionEnd || 0;
            const value = textarea.value || "";
            textarea.value = value.slice(0, start) + emoji + value.slice(end);
            const nextPos = start + emoji.length;
            if (textarea.setSelectionRange) textarea.setSelectionRange(nextPos, nextPos);
            $(textarea).trigger("input").trigger("focus");
            this.closeEmojiPanel();
        },

        notifyScreenshotTaken: function () {
            const self = this;
            if (self.state.screenshotNoticeLock || !self.state.chatId) return;
            self.state.screenshotNoticeLock = true;

            $.ajax({
                url: self.config.screenshotMessageAction,
                type: "POST",
                data: { chatId: self.state.chatId },
                complete: function () {
                    window.setTimeout(() => { self.state.screenshotNoticeLock = false; }, 1500);
                    self.syncNewMessages(true);
                }
            });
        },

        normalizeMessageGroups: function () {
            const self = this;
            const $items = this.getMessagesArea().children(".chat-message-item");

            $items.removeClass("is-group-first is-group-middle is-group-last is-single show-sender-info");

            $items.each(function (index) {
                const $item = $(this);
                if ($item.hasClass("is-action-card") || $item.hasClass("is-object-card") || $item.hasClass("is-system-notification")) return true;

                const currentKey = self.getMessageGroupKeyFromElement($item);
                const $prev = $items.eq(index - 1);
                const $next = $items.eq(index + 1);
                const prevIsMessage = $prev.length > 0 && !$prev.hasClass("is-action-card") && !$prev.hasClass("is-object-card") && !$prev.hasClass("is-system-notification");
                const nextIsMessage = $next.length > 0 && !$next.hasClass("is-action-card") && !$next.hasClass("is-object-card") && !$next.hasClass("is-system-notification");

                const samePrev = prevIsMessage && self.getMessageGroupKeyFromElement($prev) === currentKey;
                const sameNext = nextIsMessage && self.getMessageGroupKeyFromElement($next) === currentKey;

                if (!samePrev && !sameNext) $item.addClass("is-single show-sender-info");
                else if (!samePrev && sameNext) $item.addClass("is-group-first");
                else if (samePrev && sameNext) $item.addClass("is-group-middle");
                else $item.addClass("is-group-last show-sender-info");

                return true;
            });
        },

        getMessageGroupKeyFromElement: function ($item) {
            if (!$item || $item.length === 0 || $item.hasClass("is-action-card") || $item.hasClass("is-object-card") || $item.hasClass("is-system-notification")) return "";
            return `${String($item.data("member") || "")}|${String($item.data("date") || "")}`;
        },

        ensureFloatingDateBadge: function () {
            const $messagesArea = this.getMessagesArea();
            let $badge = $messagesArea.children(".chat-floating-date-badge").first();

            if ($messagesArea.length === 0) return $();

            if ($badge.length === 0) {
                $badge = $('<div class="chat-floating-date-badge" aria-hidden="true"><span></span></div>');
            }
            if (!$badge.is($messagesArea.children().first())) {
                $badge.prependTo($messagesArea);
            }

            return $badge;
        },

        updateActiveDateBadge: function () {
            const $messagesArea = this.getMessagesArea();
            if ($messagesArea.length === 0) return;

            const $items = $messagesArea.children(".chat-message-item[data-date]").filter(function () {
                return !!$(this).data("date");
            });
            const $badge = this.ensureFloatingDateBadge();

            if ($items.length === 0) {
                $badge.removeClass("is-visible");
                return;
            }

            const scrollTop = $messagesArea.scrollTop() || 0;
            let activeDate = $items.first().data("date") || "";
            const threshold = scrollTop + 42;

            $items.each(function () {
                if ((this.offsetTop || 0) <= threshold) {
                    activeDate = $(this).data("date") || activeDate;
                    return true;
                }
                return false;
            });

            if (!activeDate) {
                $badge.removeClass("is-visible");
                return;
            }

            $badge
                .attr("data-date", activeDate)
                .addClass("is-visible")
                .find("span")
                .text(this.formatDisplayDate(activeDate));
        },

        handleScrollMotion: function (scrollTop) {
            const self = this;
            const $messagesArea = self.getMessagesArea();
            if ($messagesArea.length === 0) return;

            const now = Date.now();
            const previousScrollTop = self.state.lastScrollTop || 0;
            const previousTime = self.state.lastScrollTime || now;
            const delta = scrollTop - previousScrollTop;
            const elapsed = Math.max(now - previousTime, 16); // Safe clamp for frame drops

            self.state.lastScrollTop = scrollTop;
            self.state.lastScrollTime = now;

            if (Math.abs(delta) < 0.1) return;

            const isTouchDevice = window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
            const scrollHeight = $messagesArea.prop("scrollHeight") || 0;
            const clientHeight = $messagesArea.innerHeight() || 0;
            const maxScrollTop = Math.max(scrollHeight - clientHeight, 0);

            const isAtTopEdge = scrollTop <= 2;
            const isAtBottomEdge = scrollTop >= (maxScrollTop - 2);
            const isFastScroll = Math.abs(delta / elapsed) > 0.75;

            // Sınır çizgilerinde elastik sıçramayı yumuşat/kes
            if (isTouchDevice && isFastScroll && (isAtTopEdge || isAtBottomEdge)) {
                self.state.targetShift = 0;
                self.state.targetScale = 1;
                self.state.targetRotate = 0;
                return;
            }

            const velocity = delta / elapsed;
            const maxShift = isTouchDevice ? 7 : 12;
            const minScale = isTouchDevice ? 0.989 : 0.982;
            const maxRotate = isTouchDevice ? 0.45 : 0.7;

            // Doğrudan DOM'a basmak yerine "Hedef Değerleri" set ediyoruz
            self.state.targetShift = Math.max(Math.min(velocity * -11, maxShift), -maxShift);
            self.state.targetScale = Math.max(minScale, 1 - Math.min(Math.abs(velocity) / 24, 1 - minScale));
            self.state.targetRotate = Math.max(Math.min(velocity * -0.65, maxRotate), -maxRotate);

            // Eğer enterpolasyon döngüsü (RAF) çalışmıyorsa ateşle
            if (!self.state.isMotionLoopRunning) {
                self.state.isMotionLoopRunning = true;
                $messagesArea.addClass("chat-scrolling");
                self.startMotionLoop();
            }

            // Scroll durduğu an hedefleri hızla sıfıra çek (Sıvı sönümlenme efekti)
            if (self.state.scrollStopTimer) window.clearTimeout(self.state.scrollStopTimer);
            self.state.scrollStopTimer = window.setTimeout(() => {
                self.state.targetShift = 0;
                self.state.targetScale = 1;
                self.state.targetRotate = 0;
            }, 40);
        },

        startMotionLoop: function () {
            const self = this;
            const $messagesArea = self.getMessagesArea();
            if ($messagesArea.length === 0) {
                self.state.isMotionLoopRunning = false;
                return;
            }

            // İlk değer güvenliği
            if (self.state.currentShift === undefined) self.state.currentShift = 0;
            if (self.state.currentScale === undefined) self.state.currentScale = 1;
            if (self.state.currentRotate === undefined) self.state.currentRotate = 0;

            const lerpLoop = () => {
                // Lerp Faktörü (0.12 = Üst düzey akıcı, sıvımsı amortisör etkisi)
                const lerpFactor = 0.12;

                self.state.currentShift += (self.state.targetShift - self.state.currentShift) * lerpFactor;
                self.state.currentScale += (self.state.targetScale - self.state.currentScale) * lerpFactor;
                self.state.currentRotate += (self.state.targetRotate - self.state.currentRotate) * lerpFactor;

                // Orijinal baseline durumuna tam oturduk mu kontrolü
                const isShiftStable = Math.abs(self.state.currentShift - self.state.targetShift) < 0.05;
                const isScaleStable = Math.abs(self.state.currentScale - self.state.targetScale) < 0.001;
                const isRotateStable = Math.abs(self.state.currentRotate - self.state.targetRotate) < 0.05;

                if (self.state.targetShift === 0 && isShiftStable && isScaleStable && isRotateStable) {
                    // Tam sıfırlama yap, DOM sınıflarını temizle ve döngüyü sonlandır (CPU Koruması)
                    self.state.currentShift = 0;
                    self.state.currentScale = 1;
                    self.state.currentRotate = 0;
                    self.clearScrollMotion();
                    self.state.isMotionLoopRunning = false;
                    return;
                }

                // Hesaplanan akıcı değerleri DOM elemanlarına güvenli biçimde yansıt
                $messagesArea.find(".chat-bubble").each(function () {
                    this.style.setProperty("--chat-scroll-shift", `${self.state.currentShift.toFixed(2)}px`);
                    this.style.setProperty("--chat-scroll-scale", self.state.currentScale.toFixed(3));
                    this.style.setProperty("--chat-scroll-rotate", `${self.state.currentRotate.toFixed(2)}deg`);
                });

                // Bir sonraki kare için döngüyü sürdür
                self.state.scrollAnimationFrame = window.requestAnimationFrame(lerpLoop);
            };

            if (self.state.scrollAnimationFrame) window.cancelAnimationFrame(self.state.scrollAnimationFrame);
            self.state.scrollAnimationFrame = window.requestAnimationFrame(lerpLoop);
        },

        clearScrollMotion: function () {
            const $messagesArea = this.getMessagesArea();
            $messagesArea.removeClass("chat-scrolling");
            $messagesArea.find(".chat-bubble").each(function () {
                this.style.removeProperty("--chat-scroll-shift");
                this.style.removeProperty("--chat-scroll-scale");
                this.style.removeProperty("--chat-scroll-rotate");
            });
        },

        bindTimeRevealEvents: function () {
            const self = this;
            const area = self.getMessagesArea().get(0);
            self.unbindTimeRevealEvents();
            if (!area) return;

            const supportsPointer = !!window.PointerEvent;
            const handlers = { supportsPointer, area };

            if (supportsPointer) {
                handlers.pointerdown = (event) => self.onTimeRevealStart(event.clientX, event.clientY, event.pointerId, event);
                handlers.pointermove = (event) => {
                    if (self.state.timeRevealPointerId !== null && event.pointerId !== self.state.timeRevealPointerId) return;
                    self.onTimeRevealMove(event.clientX, event.clientY, event);
                };
                handlers.pointerup = (event) => {
                    if (self.state.timeRevealPointerId !== null && event.pointerId !== self.state.timeRevealPointerId) return;
                    self.onTimeRevealEnd(event);
                };

                area.addEventListener("pointerdown", handlers.pointerdown, { passive: true });
                window.addEventListener("pointermove", handlers.pointermove, { passive: false });
                window.addEventListener("pointerup", handlers.pointerup, { passive: true });
                window.addEventListener("pointercancel", handlers.pointerup, { passive: true });
            } else {
                handlers.touchstart = (event) => {
                    if (!event.touches || event.touches.length !== 1) return;
                    const touch = event.touches[0];
                    self.onTimeRevealStart(touch.clientX, touch.clientY, null, event);
                };
                handlers.touchmove = (event) => {
                    if (!event.touches || event.touches.length !== 1) return;
                    const touch = event.touches[0];
                    self.onTimeRevealMove(touch.clientX, touch.clientY, event);
                };
                handlers.touchend = (event) => self.onTimeRevealEnd(event);

                area.addEventListener("touchstart", handlers.touchstart, { passive: true });
                window.addEventListener("touchmove", handlers.touchmove, { passive: false });
                window.addEventListener("touchend", handlers.touchend, { passive: true });
                window.addEventListener("touchcancel", handlers.touchend, { passive: true });
            }

            self.state.timeRevealHandlers = handlers;
        },

        unbindTimeRevealEvents: function () {
            const handlers = this.state.timeRevealHandlers;
            if (!handlers || !handlers.area) {
                this.state.timeRevealHandlers = null;
                return;
            }

            if (handlers.supportsPointer) {
                handlers.area.removeEventListener("pointerdown", handlers.pointerdown);
                window.removeEventListener("pointermove", handlers.pointermove);
                window.removeEventListener("pointerup", handlers.pointerup);
                window.removeEventListener("pointercancel", handlers.pointerup);
            } else {
                handlers.area.removeEventListener("touchstart", handlers.touchstart);
                window.removeEventListener("touchmove", handlers.touchmove);
                window.removeEventListener("touchend", handlers.touchend);
                window.removeEventListener("touchcancel", handlers.touchend);
            }
            this.state.timeRevealHandlers = null;
        },

        onTimeRevealStart: function (clientX, clientY, pointerId, event) {
            const $target = event && event.target ? $(event.target) : $();
            if ($target.closest(`${this.config.formSelector}, .chat-input-container, button, input, textarea, select`).length > 0) return;
            if ($target.closest(".chat-message-item.is-action-card, .chat-message-item.is-object-card, .chat-structured-message-wrap, .globalRequestCard, .globalObjectCard").length > 0) return;

            if (this.state.timeRevealCloseTimer) {
                window.clearTimeout(this.state.timeRevealCloseTimer);
                this.state.timeRevealCloseTimer = null;
            }

            this.state.isTimeRevealDragging = true;
            this.state.isTimeRevealLocked = false;
            this.state.timeRevealPointerId = pointerId !== undefined ? pointerId : null;
            this.state.timeRevealStartX = clientX || 0;
            this.state.timeRevealStartY = clientY || 0;
        },

        onTimeRevealMove: function (clientX, clientY, event) {
            if (!this.state.isTimeRevealDragging) return;

            const dx = (clientX || 0) - this.state.timeRevealStartX;
            const dy = (clientY || 0) - this.state.timeRevealStartY;
            const absX = Math.abs(dx);
            const absY = Math.abs(dy);

            if (!this.state.isTimeRevealLocked) {
                if (absX < 8 && absY < 8) return;
                if (absY > absX || absX < absY * 1.18) {
                    this.cancelTimeRevealDrag(false);
                    return;
                }
                this.state.isTimeRevealLocked = true;
            }

            if (this.state.isTimeRevealLocked && event && event.cancelable) {
                event.preventDefault();
            }

            const progress = Math.max(Math.min(-dx / this.config.timeRevealDistancePx, 1), 0);
            this.setTimeRevealProgress(progress, false);
        },

        onTimeRevealEnd: function () {
            if (!this.state.isTimeRevealDragging) return;

            const shouldHold = this.state.isTimeRevealLocked && this.state.timeRevealProgress >= 0.35;
            this.state.isTimeRevealDragging = false;
            this.state.isTimeRevealLocked = false;
            this.state.timeRevealPointerId = null;

            if (shouldHold) {
                this.setTimeRevealProgress(1, false);
                this.scheduleTimeRevealClose();
            } else {
                this.setTimeRevealProgress(0, false);
            }
        },

        cancelTimeRevealDrag: function (closeReveal) {
            this.state.isTimeRevealDragging = false;
            this.state.isTimeRevealLocked = false;
            this.state.timeRevealPointerId = null;
            if (closeReveal) this.setTimeRevealProgress(0, false);
        },

        scheduleTimeRevealClose: function () {
            const self = this;
            if (self.state.timeRevealCloseTimer) window.clearTimeout(self.state.timeRevealCloseTimer);

            self.state.timeRevealCloseTimer = window.setTimeout(() => {
                self.state.timeRevealCloseTimer = null;
                self.setTimeRevealProgress(0, false);
            }, self.config.timeRevealHoldMs);
        },

        setTimeRevealProgress: function (progress, immediate) {
            const self = this;
            progress = Math.max(Math.min(progress || 0, 1), 0);
            self.state.timeRevealProgress = progress;

            if (self.state.timeRevealAnimationFrame) {
                window.cancelAnimationFrame(self.state.timeRevealAnimationFrame);
                self.state.timeRevealAnimationFrame = null;
            }

            const applyProgress = () => {
                const $messagesArea = self.getMessagesArea();
                const revealWidth = Math.round(self.config.timeRevealDistancePx * progress);
                const revealShift = Math.round((1 - progress) * -10);
                const revealScale = 0.94 + (progress * 0.06);
                const bubbleShift = `${(progress * -4).toFixed(2)}px`;
                const bubbleScale = (1 - (progress * 0.006)).toFixed(3);

                $messagesArea
                    .toggleClass("chat-time-reveal-visible", progress > 0.02)
                    .toggleClass("chat-time-reveal-full", progress >= 0.98)
                    .css({
                        "--chat-time-reveal-progress": progress.toFixed(3),
                        "--chat-time-reveal-width": `${revealWidth}px`,
                        "--chat-time-reveal-shift": `${revealShift}px`,
                        "--chat-time-reveal-scale": revealScale.toFixed(3),
                        "--chat-time-reveal-bubble-shift": bubbleShift,
                        "--chat-time-reveal-bubble-scale": bubbleScale
                    });
            };

            if (immediate) applyProgress();
            else self.state.timeRevealAnimationFrame = window.requestAnimationFrame(applyProgress);
        },

        getMessagesArea: function () {
            let $messagesArea = $(this.config.messagesAreaSelector);
            if ($messagesArea.length === 0) $messagesArea = $(this.config.messagesAreaFallbackSelector).first();
            return $messagesArea;
        },

        resolveChatId: function ($messagesArea) {
            return parseInt(
                $messagesArea.data("chat") || $messagesArea.data("chat-id") || $(this.config.chatIdSelector).val() || 0,
                10
            ) || 0;
        },

        resolveCurrentUserId: function () {
            const self = this;
            return new Promise((resolve, reject) => {
                const currentUserId = parseInt(
                    $(self.config.currentUserIdSelector).val() ||
                    self.getMessagesArea().data("current-user") ||
                    self.getMessagesArea().data("current-user-id") || 0, 10
                ) || 0;

                if (currentUserId > 0) {
                    resolve(currentUserId);
                    return;
                }

                if (typeof window.getCurrentUserId !== "function") {
                    reject();
                    return;
                }

                try {
                    const result = window.getCurrentUserId();
                    if (result && typeof result.then === "function") {
                        result.then(resolve, reject);
                        return;
                    }
                    resolve(result);
                } catch (error) { reject(error); }
            });
        },

        normalizeResponse: function (response) {
            if ($.isArray(response)) return { success: true, messages: response };

            if (response && typeof response === "object") {
                const hasSuccess = Object.prototype.hasOwnProperty.call(response, "success") || Object.prototype.hasOwnProperty.call(response, "Success");
                const success = hasSuccess ? !!(response.success !== undefined ? response.success : response.Success) : true;

                return {
                    success: success,
                    message: response.message || response.Message || "",
                    messages: response.messages || response.Messages || response.data || response.Data || [],
                    hasMoreOlder: response.hasMoreOlder !== undefined ? response.hasMoreOlder : response.HasMoreOlder,
                    oldestCursor: response.oldestCursor || response.OldestCursor || null,
                    newestCursor: response.newestCursor || response.NewestCursor || null,
                    currentUserIsChatAdmin: this.normalizeBoolean(
                        response.currentUserIsChatAdmin !== undefined ? response.currentUserIsChatAdmin : response.CurrentUserIsChatAdmin
                    )
                };
            }
            return { success: true, messages: [] };
        },

        normalizeCursor: function (cursor) {
            if (!cursor) return null;
            const sendDate = cursor.sendDate || cursor.SendDate || cursor.beforeSendDate || cursor.BeforeSendDate || null;
            const usersMessageId = cursor.usersMessageId || cursor.UsersMessageId || cursor.messageId || cursor.MessageId || cursor.id || cursor.Id || null;
            if (!sendDate && !usersMessageId) return null;
            return { sendDate, usersMessageId };
        },

        getCursorFromFirstMessage: function (messages) {
            if (!messages || messages.length === 0) return null;
            return this.getCursorFromMessage(messages[0]);
        },

        getCursorFromLastMessage: function (messages) {
            if (!messages || messages.length === 0) return null;
            return this.getCursorFromMessage(messages[messages.length - 1]);
        },

        getCursorFromMessage: function (message) {
            const sendDate = this.getValue(message, ["SendDate", "sendDate"]);
            const usersMessageId = this.getMessageId(message);
            if (!sendDate && !usersMessageId) return null;
            return { sendDate, usersMessageId };
        },

        getMessageId: function (message) {
            return this.getValue(message, ["UsersMessageId", "usersMessageId", "MessageId", "messageId", "Id", "id"]);
        },

        getMessageDateKey: function (message) {
            const sendDate = this.parseDate(this.getValue(message, ["SendDate", "sendDate"]));
            return sendDate ? this.formatDateKey(sendDate) : "";
        },

        getFirstMessageDateKey: function () {
            const $items = this.getMessagesArea().children(".chat-message-item[data-date]").filter(function () {
                return !!$(this).data("date");
            });
            return $items.first().data("date") || null;
        },

        getLastMessageDateKey: function () {
            const $items = this.getMessagesArea().children(".chat-message-item[data-date]").filter(function () {
                return !!$(this).data("date");
            });
            return $items.last().data("date") || null;
        },

        isStructuredMessage: function (message) {
            if (!message) return false;

            const actionId = parseInt(this.getValue(message, ["ActionId", "actionId"]) || 0, 10) || 0;
            const objectId = parseInt(this.getValue(message, ["ObjectId", "objectId"]) || 0, 10) || 0;

            return actionId > 0 || objectId > 0;
        },

        isStructuredMessageElement: function ($item) {
            if (!$item || $item.length === 0) return false;

            const actionId = parseInt($item.attr("data-action-id") || $item.data("action-id") || 0, 10) || 0;
            const objectId = parseInt($item.attr("data-object-id") || $item.data("object-id") || 0, 10) || 0;
            const isStructured = String($item.attr("data-is-structured") || "false") === "true";

            return isStructured || actionId > 0 || objectId > 0 || $item.hasClass("is-action-card") || $item.hasClass("is-object-card");
        },

        canOpenMessageInfoForItem: function ($item) {
            if (!$item || $item.length === 0) return false;
            return !this.isStructuredMessageElement($item) || $item.hasClass("is-expired-reservation-announcement");
        },

        isSystemNotification: function (message) {
            const value = this.getValue(message, ["IsSystemNotification", "isSystemNotification", "is_system_notification"]);
            if (typeof value === "boolean") return value;
            if (typeof value === "number") return value === 1;
            if (typeof value === "string") return value.toLowerCase() === "true" || value === "1";
            return false;
        },

        isMessageReported: function (message) {
            const value = this.getValue(message, ["Reported", "reported", "IsReported", "isReported"]);
            if (typeof value === "boolean") return value;
            if (typeof value === "number") return value === 1;
            if (typeof value === "string") return value.toLowerCase() === "true" || value === "1";
            return false;
        },

        isMessageEdited: function (message) {
            const value = this.getValue(message, ["IsEdited", "isEdited", "Edited", "edited"]);
            if (typeof value === "boolean") return value;
            if (typeof value === "number") return value === 1;
            if (typeof value === "string") return value.toLowerCase() === "true" || value === "1";
            return false;
        },

        extractMediaUrl: function (value) {
            if (!value) return "";
            if (typeof value === "string") return value;
            if (typeof value === "object") {
                return value.FileUrl || value.fileUrl || value.Url || value.url || value.Path || value.path || value.ImageUrl || value.imageUrl || value.VideoUrl || value.videoUrl || "";
            }
            return "";
        },

        messageHasImage: function (message) {
            const explicit = this.getValue(message, ["HasImage", "hasImage"]);
            if (explicit !== null && explicit !== undefined) {
                if (typeof explicit === "boolean") return explicit;
                if (typeof explicit === "number") return explicit === 1;
                if (typeof explicit === "string") return explicit.toLowerCase() === "true" || explicit === "1";
            }
            return !!this.extractMediaUrl(this.getValue(message, ["Image", "image", "ImageUrl", "imageUrl"]));
        },

        messageHasVideo: function (message) {
            const explicit = this.getValue(message, ["HasVideo", "hasVideo"]);
            if (explicit !== null && explicit !== undefined) {
                if (typeof explicit === "boolean") return explicit;
                if (typeof explicit === "number") return explicit === 1;
                if (typeof explicit === "string") return explicit.toLowerCase() === "true" || explicit === "1";
            }
            return !!this.extractMediaUrl(this.getValue(message, ["Video", "video", "VideoUrl", "videoUrl"]));
        },

        messageHasFile: function (message) {
            const explicit = this.getValue(message, ["HasFile", "hasFile"]);
            if (explicit !== null && explicit !== undefined) {
                if (typeof explicit === "boolean") return explicit;
                if (typeof explicit === "number") return explicit === 1;
                if (typeof explicit === "string") return explicit.toLowerCase() === "true" || explicit === "1";
            }
            return !!this.extractMediaUrl(this.getValue(message, ["File", "file", "FileUrl", "fileUrl"]));
        },

        applyFontSizeUtilityClasses: function () {
            const $root = this.getMessagesArea().add($(this.config.chatInputSelector));
            const map = [
                [".chat-system-notification-time, .chat-reveal-time, .chat-time, .chat-member-name, .chat-reported-badge, .chat-edited-label, .chat-delete-reason, .chat-edit-reason, .chat-report-reason, .chat-message-member-row small", "fs-06"],
                [".chat-system-notification-text, .chat-loader, .chat-top-loader, .chat-file-link, .chat-selected-files, .chat-attachment-menu-btn, .chat-message-action-loader, .chat-message-action-error, .chat-message-member-empty, .chat-message-member-section-title span, .chat-message-member-list, .chat-message-secondary-action, .chat-report-message, .chat-delete-for-everyone, .chat-withdraw-report, .chat-edit-cancel-mode-btn", "fs-07"],
                [".chat-text, .chat-message-action-title", "fs-08"],
                ["#messageText, .chat-empty-state strong, .chat-emoji-option", "fs-09"]
            ];

            map.forEach(([selector, className]) => {
                $root.find(selector).addClass(className);
            });
        },

        normalizeBoolean: function (value) {
            if (value === true || value === false) return value;
            if (value === null || value === undefined) return false;
            if (typeof value === "number") return value !== 0;
            if (typeof value === "string") {
                const normalized = value.trim().toLowerCase();
                return normalized === "true" || normalized === "1" || normalized === "yes" || normalized === "evet";
            }
            return !!value;
        },

        isCurrentUserChatAdmin: function () {
            return !!this.state.currentUserIsChatAdmin;
        },

        canDeleteMessageForCurrentUser: function (payload) {
            return this.isCurrentUserChatAdmin() || !!(payload && payload.canDeleteForEveryone);
        },

        getValue: function (obj, keys) {
            if (!obj) return null;
            for (let i = 0; i < keys.length; i++) {
                if (obj[keys[i]] !== undefined && obj[keys[i]] !== null) return obj[keys[i]];
            }
            return null;
        },

        getErrorMessage: function (xhr, fallbackMessage) {
            if (xhr && xhr.responseJSON) return xhr.responseJSON.message || xhr.responseJSON.Message || fallbackMessage;
            if (xhr && xhr.responseText) return xhr.responseText;
            return fallbackMessage || "İşlem sırasında hata oluştu.";
        },

        showError: function (message) {
            if (typeof window.ShowErrorAlert === "function") {
                window.ShowErrorAlert(message);
                return;
            }
            window.console && window.console.error(message);
        },

        parseDate: function (value) {
            if (!value) return null;
            return SahaseninTime.parse(value);
        },

        formatTime: function (date) {
            return SahaseninTime.format(date, { hour: '2-digit', minute: '2-digit', hour12: false });
        },

        formatDateKey: function (date) {
            return SahaseninTime.dateKey(date);
        },

        formatDisplayDate: function (dateKey) {
            const date = this.parseDate(`${dateKey}T00:00:00`);
            if (!date) return dateKey || "";
            return SahaseninTime.format(date, { year: "numeric", month: "2-digit", day: "2-digit" });
        },

        pad2: function (value) {
            value = parseInt(value, 10) || 0;
            return value < 10 ? `0${value}` : String(value);
        },

        formatMessageText: function (text) {
            return this.safeText(text).replace(/\r\n|\r|\n/g, "<br>");
        },

        safeText: function (text) {
            if (text === null || text === undefined) return "";
            return String(text)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        },

        safeAttr: function (text) {
            return this.safeText(text);
        },

        escapeJquerySelectorValue: function (value) {
            value = String(value);
            if ($.escapeSelector) return $.escapeSelector(value);
            return value.replace(/([ #;?%&,.+*~\':"!^$[\]()=>|\/\\@])/g, "\\$1");
        }
    };

    window.ChatModule = ChatModule;
    window.ChatManager = ChatModule;
    window.NotifyChatScreenshotTaken = function () { ChatModule.notifyScreenshotTaken(); };

    $(document).ready(function () {
        ChatModule.init();
    });
})(window, document, window.jQuery);


async function updateGlobalObjectCards() {

    var elements = $('.globalObjectCard:not(.init)');

    if (elements.length > 0) {

        elements.addClass('init');

        const requests = [];

        elements.each(function () {

            var element = $(this);

            requests.push(
                Request({
                    url: '/UsersMessage/_objectCard',
                    type: 'POST',
                    data: {
                        id: element.data('id'),
                        type: element.data('type')
                    }
                }).then(response => {
                    element.html(response);
                }).catch(() => {
                    element.addClass('d-none');
                })
            );
        });

        await Promise.all(requests);

        syncMatchCountDown();
    }
}

$(document).on("click", ".MuteUnmuteChatBtn", function (e) {
    e.preventDefault();

    var btn = $(this);

    if (btn.data("loading"))
        return;

    var chatId = btn.data("chat");
    var currentValue = String(btn.attr("data-val")).toLowerCase() === "true";
    var newValue = !currentValue;
    var url = btn.data("url");

    btn.data("loading", true);

    $.ajax({
        url: url,
        type: "POST",
        data: {
            chatId: chatId,
            muteChat: newValue
        },
        success: function () {

            btn.attr("data-val", newValue ? "True" : "False");

            var icon = btn.find("i");

            if (newValue) {
                btn.addClass("text-primary");
                icon.addClass("fa-bell").removeClass("fa-bell-slash");
            } else {
                btn.removeClass("text-primary");
                icon.addClass("fa-bell-slash").removeClass("fa-bell");
            }

            btn.contents().filter(function () {
                return this.nodeType === 3;
            }).last()[0].textContent = newValue
                    ? " Sesi Aç"
                    : " Sessize Al";
        },
        error: function (xhr) {
            alert(xhr.responseText || "Bir hata oluştu.");
        },
        complete: function () {
            btn.data("loading", false);
        }
    });
});
